import { saveRunArchive, readRunArchive, localArchiveFrame } from "./helpers/RunArchive";
import { batch, computed, effect, signal } from "@preact/signals";
import { BackendURI } from "./config";
import BinaryReader from "./helpers/BinaryReader";
import { PlantModel } from "./helpers/Scene";
import * as SignalR from "@microsoft/signalr";
import { Seed } from "./helpers/Seed";
import { Obstacle } from "./helpers/Obstacle";
import { BaseRequestObject } from "./helpers/BaseRequestObject";
import { VisualMappingOptions } from "./render/core/PlantColors";
import { activeRenderer } from "./render/IViewportRenderer";
import { vec2, addScaled2 } from "./render/core/Vec3";
import { downloadBinary, downloadBlob, downloadText, timestampedName } from "./helpers/download";
import {
    IObstacleLightDocument, isObstacleLightDocument,
    obstacleValueOn, ObstacleMappingOptions, obstacleMappingSpec, stepDate,
} from "./helpers/ObstacleLight";
import { canDeflate, deflateBytes, inflateBytes } from "./helpers/deflate";
import { ALTITUDE, AZIMUTH, AZIMUTH_OFFSETS, DEFAULT_INITIAL_DATE, LATITUDE, LONGITUDE, SolarSite } from "./helpers/Sun";
import { convertCoordsTo_RH_rightPX_upPY } from "./helpers/CoordinateSystem";
import { LIGHTING_OFF, LightingOptions, SHADOW_NORMAL_OFFSET_TEXELS, lightingFromLegacyTier } from "./render/core/Lighting";
import { Species } from "./helpers/Species";
import { IObjImport, Parse } from "./helpers/ObjParser";
import { BoxTerrainItem, ITerrainItem, MeshTerrainItem } from "./helpers/Terrain";
import { CoordinateSystem } from "./helpers/CoordinateSystem";
import { IStoredRun, camelizeTopLevel } from "./helpers/StoredRun";

interface RetryContext {
    readonly previousRetryCount: number; //The number of consecutive failed tries so far.
    readonly elapsedMilliseconds: number; // The amount of time in milliseconds spent retrying so far.
    readonly retryReason: Error; // The error that forced the upcoming retry.
}

const InfiniteHubRetry = {
    nextRetryDelayInMilliseconds(retryContext: RetryContext): number | null {
        switch (retryContext.previousRetryCount)
        {
            case 0: case 1: return 200;
            case 2: case 3: return 500;
            case 4: case 5: case 6: case 7: return 1000;
            case 8: case 9: case 10: case 11: return 2000;
            default: return 5000;
        }
    }
}

const hubConnection = new SignalR.HubConnectionBuilder().withUrl(`${location.protocol}//${BackendURI}/SimSocket`).withAutomaticReconnect(InfiniteHubRetry).build();
//"rejected", not "reject". The server method is Rejected() and the SignalR JS client lower-cases
//handler names, so the old registration never matched and a refused run told the user nothing at
//all - the Simulate button simply did nothing.
hubConnection.on("rejected", () => {
    console.warn("A simulation is already running on this connection. Wait for it to finish, or stop it first.");
    st.computing.value = false;
});
hubConnection.on("progress", (step: number, length: number) => {
    batch(() => {
        st.simStep.value = step + 1;
        st.simLength.value = length;
    });
    //The server is usually well ahead of what this tab holds. It exports and stores every step
    //while the wire sends a frame only when the browser asks for one, and at several thousand
    //plants the browser asks rarely - so the slider used to span the handful of frames that had
    //arrived while the counter below it said 550/1080. Same run, two numbers, and the honest one
    //was the small print.
    maybeSyncTimeline(step + 1);
});

/**
 * Keeps the timeline in step with the run WHILE it computes.
 *
 * The index is eight bytes per frame, so catching up on a 1,080-step run costs about 8 KB - and
 * what it buys is a slider that spans everything computed so far, with each payload fetched only
 * if the user actually scrubs onto it. That is the whole "keep `t`, stream `s`" idea applied to a
 * live run rather than only to a finished one.
 *
 * Throttled on both sides: never more than one request in flight, never more often than every
 * couple of seconds, and only when the run is actually ahead of what is held. `Progress` arrives
 * once per simulated step, which at a large stand is far too often to fetch anything.
 */
/**
 * Settles when the startup species fetch has finished, however it finished.
 *
 * Anything that REPLACES the species list has to wait for it. The fetch is fired from the module
 * body and assigns `st.species.value` when it lands, so a stored run applied before then has its
 * species silently overwritten a moment later by the defaults - and the plants are then drawn with
 * the wrong morphology, with nothing having failed. Declared here, and only assigned at the fetch,
 * so awaiting it before that point is a no-op rather than a hazard.
 */
let speciesReady: Promise<unknown> = Promise.resolve();

let timelineSyncing = false;
let lastTimelineSync = 0;
const TIMELINE_SYNC_INTERVAL_MS = 2000;
const TIMELINE_SYNC_LAG_FRAMES = 4;

function maybeSyncTimeline(serverSteps: number) {
    const runId = st.runId.peek();
    if (!runId || timelineSyncing) return;
    if (serverSteps - st.history.length < TIMELINE_SYNC_LAG_FRAMES) return;
    const now = Date.now();
    if (now - lastTimelineSync < TIMELINE_SYNC_INTERVAL_MS) return;
    lastTimelineSync = now;
    timelineSyncing = true;
    //`false`, so a user who has scrubbed back to look at something is not yanked to the newest
    //frame every two seconds.
    void syncTimelineToIndex(runId, false).finally(() => { timelineSyncing = false; });
}

hubConnection.on("result", (result: ISimResponse) => {
    if (result.debug?.length > 0)
        console.log(result.debug);

    const step = result.stepTimes.length - 1;
    if (st.history.length == 0 || st.history[st.history.length - 1].t < step)
    {
        //Convert once and retain the BYTES, not the base64 the wire delivered. The string is
        //4/3 the size for no benefit, and dropping it here lets it be collected immediately
        //instead of being held for the rest of the session.
        const binaryScene = base64ToArrayBuffer(result.scene);
        lastReceivedIndex = upsertHistoryFrame(step, binaryScene);
        const reader = new BinaryReader(binaryScene);
        const scene = reader.readAgroScene();

        //console.log(result.stepTimes);
        batch(() => {
            st.plants.value = result.plants;
            st.scene.value = scene;
            st.computing.value = false;
            st.renderer.value = result.renderer;
            st.simLength.value = 0;
            st.historyCount.value = st.history.length;
            st.obstacleLight.value = result.obstacleLight ?? undefined;
        });
    }
    else
    {
        //console.log(result.stepTimes);
        batch(() => {
            st.plants.value = result.plants;
            st.computing.value = false;
            st.renderer.value = result.renderer;
            st.simLength.value = 0;
            st.obstacleLight.value = result.obstacleLight ?? undefined;
        });
    }

    //Stop waiting for frames that are not coming.
    //
    //The playback machine cannot do this for itself. It only advances when something wakes it,
    //and the thing that wakes it is a frame arriving - so after the last one it simply stays in
    //ForwardWaiting for the rest of the session, which leaves a finished run reading as "playing"
    //with the step controls disabled over a history that is entirely there.
    if (st.playing.peek() === PlayState.ForwardWaiting)
        batch(() => {
            st.playing.value = PlayState.None;
            st.playPointer.value = Math.max(0, Math.min(st.playPointer.peek(), st.history.length - 1));
        });

    //The run is over and the stored timeline is final, so take it whole.
    //
    //The wire is deliberately lazy - a frame is sent only when the client asks for one - while
    //the server exports and stores EVERY step. So the history built up during a run is whatever
    //the browser managed to keep up with, and a run that stored 1,440 frames may have streamed
    //sixty. The rest were on disk and unreachable from the page that asked for them. Now they
    //are simply frames whose payload has not been fetched yet, like any evicted one.
    const finishedRun = st.runId.peek();
    if (finishedRun) void syncTimelineToIndex(finishedRun, false);
});

//The server stores every frame it computes, so once a run id arrives the client no longer has
//to be the only place the history exists.
hubConnection.on("runStarted", (runId: string) => {
    st.runId.value = runId;
    //There is somewhere to put the frames now, so stop hoarding them here.
    refreshHistoryBudget();
    //Survives a reload. Without it the id is lost, resubscribe() has nothing to rejoin, and the
    //run's stored frames become unreachable from the page that started them.
    try { sessionStorage.setItem('agro.runId', runId); } catch { /* private mode, not fatal */ }
});

/**
 * Where the run is and what weather it will see, sent once just after `runStarted`.
 *
 * Both are run-level facts the viewport needs per TIMESTEP, which is exactly why they arrive
 * this way instead of riding the frame format: the playhead can land on a step whose frame was
 * evicted, and an offline export renders steps it never puts the playhead on at all.
 *
 * The time zone in particular can only come from here. The server derives it from the
 * coordinates and no browser API does the same, so a client that guessed would shift every sun
 * position, every shadow and the whole sky by the offset while still looking plausible.
 */
hubConnection.on("runSite", (site: ISimRunSite) => {
    batch(() => {
        st.latitude.value = site.latitude;
        st.longitude.value = site.longitude;
        st.altitude.value = site.altitude;
        st.azimuthBase.value = site.azimuthBase ?? st.azimuthBase.peek();
        st.azimuthOffset.value = site.azimuthOffset ?? st.azimuthOffset.peek();
        st.siteTimeZone.value = site.timeZone || undefined;
        st.weather.value = decodeSkyCoverage(site.skyCoverage);
    });
});

/**
 * Raw little-endian float32, one per timestep, base64 over the wire as the frames are.
 *
 * Copied into a fresh Float32Array rather than wrapped in place: base64ToArrayBuffer has no
 * alignment guarantee, and a Float32Array view needs its offset to be a multiple of four.
 */
function decodeSkyCoverage(base64: string | undefined): Float32Array | undefined {
    if (!base64) return undefined;
    const bytes = base64ToArrayBuffer(base64);
    const count = Math.floor(bytes.byteLength / 4);
    if (count === 0) return undefined;
    const out = new Float32Array(count);
    new Uint8Array(out.buffer).set(bytes.subarray(0, count * 4));
    return out;
}

hubConnection.on("preview", (result: ISimPreview) => {
    const binaryScene = base64ToArrayBuffer(result.scene);
    //Filed by step, not appended: the timeline may already know about this step from the run
    //index. `historySize` is updated inside, and before eviction runs - otherwise the loop
    //weighs a total that is missing the very frame that pushed it over.
    lastReceivedIndex = upsertHistoryFrame(result.step, binaryScene);
    st.historyCount.value = st.history.length;
    scheduleCompression();

    //Over budget, drop the oldest payloads - the server has them. Only when it does NOT (an old
    //server, or a session with no run behind it) is there nothing to do but stop the run, which
    //at least leaves a saveable session rather than a dead tab.
    //Read the signal, not the function that seeded it: the HUD measures against the signal, so
    //using the raw function here would let the two disagree - and it would ignore any budget the
    //user or a test had set.
    if (st.historySize.peek() > st.historyBudget.peek()) {
        if (st.runId.peek()) evictOldestPayloads();
        else st.stopForMemory();
    }

    if (st.playing.peek() == PlayState.ForwardWaiting)
    {
        st.previewRequestAfterSceneUpdate = true;
        const reader = new BinaryReader(binaryScene);
        const scene = reader.readAgroScene();
        batch(() => {
            st.scene.value = scene;
            st.renderer.value = result.renderer;
        });
    }
    else
    {
        batch(() => {
            st.renderer.value = result.renderer;
            st.previewRequest.value = true;
        });
    }
});

hubConnection.on("terrain", (response: Uint8Array) => {
    const binaryTerrain = base64ToArrayBuffer(response);
    const reader = new BinaryReader(binaryTerrain);
    const result = reader.readTerrain();
    st.terrainList = result.terrains;
    st.obstacles.value = [...st.obstacles.value.filter(x => x.type.value !== 'mesh'), ...result.obstacles];
    st.bumpTerrain();
})

/**
 * Whether the hub is usable, as a signal.
 *
 * `hubConnection.state` is a plain field, so an effect that consulted it could not be woken
 * when it changed - and a preview request made before the socket came up was simply dropped.
 * Because `previewRequest` was already `true`, setting it `true` again was not a change and
 * did not re-run the effect either, so the request loop stalled for the rest of the session:
 * the simulation ran to completion server-side and the viewport only updated at the very end.
 * Whether that race was lost came down to how fast the browser opened the WebSocket, which is
 * why it appeared in Firefox and not in Chromium.
 */
const hubConnected = signal(false);

const start = async() => {
    try {
        await hubConnection.start();
    } finally {
        hubConnected.value = hubConnection.state === SignalR.HubConnectionState.Connected;
        //Also here, not only in onreconnected. `onclose` falls back to start(), which opens a
        //brand-new connection with a new id - so without this a hard drop would leave the run
        //ticking server-side with this tab silently detached from its group.
        if (hubConnected.value) void resubscribe();
    }
};
hubConnection.onclose(() => { hubConnected.value = false; start(); });
hubConnection.onreconnecting(() => { hubConnected.value = false; });
hubConnection.onreconnected(() => { hubConnected.value = true; resubscribe(); });

/**
 * Rejoins the run after the socket came back.
 *
 * A run no longer belongs to a connection - it belongs to the runner, and its frames go to a
 * SignalR group named by the run id. A reconnect gets a NEW connection id, so without rejoining
 * that group the run would carry on server-side while this tab heard nothing more from it.
 */
async function resubscribe() {
    if (!st.runId.peek()) {
        try {
            const saved = sessionStorage.getItem('agro.runId');
            if (saved) st.runId.value = saved;
        } catch { /* private mode */ }
    }
    const runId = st.runId.peek();
    if (!runId) return;

    //Rebuild the timeline BEFORE rejoining. Frames arriving from here on are appended, so an
    //empty history would start the run again at whatever step the reload happened to land on and
    //everything computed before it would be unreachable from the page that asked for it.
    const restored = st.history.length === 0 ? await syncTimelineToIndex(runId, true) : 0;

    try {
        const ok = await hubConnection.invoke('subscribe', runId);
        if (!ok) {
            //Finished, evicted, or from a previous server process. Its frames may still be
            //readable - which is exactly what `restored` just established - but nothing further
            //will arrive on the socket.
            const held = st.history.length;
            console.warn(held > 0
                ? `Run ${runId} is no longer computing; its ${held} stored frames remain readable.`
                : `Run ${runId} is gone from the server.`);
            //Only when there is genuinely nothing left. `restored` is zero on the second pass
            //through here too - recoverRun() hydrates first and then opens the socket, which
            //re-enters this function with a history it has already rebuilt.
            if (held === 0) forgetRun();
        }
    } catch (err) {
        console.warn('Could not rejoin the run after reconnecting:', err);
    }
}

/**
 * Drops this tab's association with a server-side run.
 *
 * Worth doing explicitly rather than leaving a dead id in place. Eviction is only safe because
 * the server is assumed to hold what it drops, so an id that no longer resolves turns "the server
 * has it" into silent data loss - and a re-fetch against a run that has been replaced returns the
 * same step of a different simulation, which decodes perfectly and is simply the wrong plants.
 */
function forgetRun() {
    st.runId.value = '';
    //Nothing behind the client any more: a dropped frame would be gone, so go back to holding on.
    refreshHistoryBudget();
    try { sessionStorage.removeItem('agro.runId'); } catch { /* private mode */ }
}

/**
 * Reattaches to the run this tab started, after a reload.
 *
 * Nothing did this before, and the socket is not opened on page load either, so a reload during a
 * long run left the simulation computing server-side with no one listening until the unwatched
 * grace period stopped it - while the tab showed an empty timeline and no hint that a run existed.
 * The frames were on disk the whole time.
 *
 * Deliberately quiet: no run id, no server, or a run the server has forgotten all end with the
 * page in exactly the state it would have been in anyway.
 */
async function recoverRun() {
    let saved = '';
    try { saved = sessionStorage.getItem('agro.runId') ?? ''; } catch { return; }
    if (!saved) return;

    const meta = await fetchStoredRun(saved);
    if (!meta) { forgetRun(); return; }

    st.runId.value = saved;
    refreshHistoryBudget();
    //The clock multiplies a frame's step by this, so without it a recovered run is drawn on the
    //wrong timeline - and it is not otherwise recoverable from the page.
    st.simHoursPerTick = meta.hoursPerTick;
    //The soil, which a reload loses with everything else in the tab: it arrives over the socket at
    //upload time and is never part of a frame. Without it a recovered run - one still computing
    //just as much as a finished one - draws its plants floating in an empty field, which reads as
    //a broken scene rather than as a reload. The store keeps it for exactly this.
    await st.restoreStoredTerrain(saved, meta.hasTerrain, meta.fieldModelName);
    //And the scenario behind it. A reload empties the editor, so without this the run comes back
    //as frames over the right trays with somebody else's species, seeds and length beside them -
    //and Fork from that state would carry the defaults rather than the run.
    try { await st.restoreStoredScenario(saved); }
    catch (err) { console.warn(`Could not restore the scenario of run ${saved}:`, err); }
    if (await syncTimelineToIndex(saved, true) === 0) { forgetRun(); return; }

    //A finished run needs no socket at all: its timeline is final and its frames come over REST.
    //That is the common case - reloading to look at a run that has ended - and it should not
    //open a websocket to do nothing.
    if (meta.status !== 'running') return;

    //Still computing, so put the tab back into the state it was in before the reload.
    //
    //Rejoining the group is NOT enough to resume streaming. The wire is deliberately lazy: the
    //server sends a frame only while the client is asking for one, and a freshly loaded page is
    //asking for nothing. Without this the reattached tab sits on a timeline frozen at the moment
    //of the reload while the run computes on, which looks exactly like a failed reattach.
    if (hubConnection.state !== SignalR.HubConnectionState.Connected) await start();
    batch(() => {
        st.computing.value = true;
        st.simLength.value = meta.totalSteps;
        //The step reached, not the number of files. They are the same only at a stride of one,
        //and with a stride the counter would read a quarter of the true progress.
        st.simStep.value = meta.lastStep;
        st.playing.value = PlayState.ForwardWaiting;
    });
    st.previewRequest.value = true;
}

/**
 * Gives freshly loaded species their leaf and petal outlines back.
 *
 * `Species.load()` copies settings and nothing else: `leafMeshSlices` and `petalMesh` are filled
 * only by the one-shot fetch at startup, against the instances that existed then. So replacing the
 * list - which both opening a stored run and loading a session do - leaves every plant drawing the
 * `Geom.Plane` fallback quad for every leaf and every petal, silently and with nothing in the UI
 * to say why.
 *
 * Copied from the previous same-named instance where there is one, because that costs nothing and
 * covers the common case: a stored run almost always uses the species the page already has. Only a
 * name the page has never seen goes to the network, and a failure there leaves exactly the fallback
 * that would have happened anyway.
 */
function adoptSpeciesGeometry(loaded: Species[], previous: Species[]): Species[] {
    const known = new Map(previous.map(s => [s.name.peek(), s]));
    const missing: Species[] = [];
    for (const species of loaded) {
        const old = known.get(species.name.peek());
        if (old && (old.leafMeshSlices.length > 0 || old.petalMesh)) {
            species.leafMesh = old.leafMesh;
            species.leafMeshSlices = old.leafMeshSlices;
            species.petalMesh = old.petalMesh;
        }
        else missing.push(species);
    }
    if (missing.length > 0) void fetchSpeciesGeometry(missing);
    return loaded;
}

/**
 * Fetches the leaf slices and petal outline for species the page has no geometry for.
 *
 * The slice set rather than the single whole-leaf mesh: a long blade is a chain of leaf organs and
 * each needs its own piece of the outline, or the blade pinches to a point at every joint.
 */
async function fetchSpeciesGeometry(species: Species[]): Promise<void> {
    await Promise.all(species.flatMap(s => [
        fetch(`${location.protocol}//${BackendURI}/Simulation/mesh/leaf/${s.name}/slices`)
            .then(r => r.ok ? r.arrayBuffer() : undefined).then(b => b && s.loadLeafSlices(b))
            .catch(() => { /* the fallback quad is what happens anyway */ }),
        fetch(`${location.protocol}//${BackendURI}/Simulation/mesh/petal/${s.name}`)
            .then(r => r.ok ? r.arrayBuffer() : undefined).then(b => b && s.loadPetalGeometry(b))
            .catch(() => { /* ditto */ }),
    ]));
    //The species objects are mutated in place, so nothing re-runs on its own.
    st.needsRender.value = true;
}

/**
 * How many stored runs one listing asks for. The server clamps to 500.
 *
 * A page, not the store: the panel says so when it comes back full, because the total it prints is
 * the number a user weighs before deleting anything and "200 runs, 31 GB" for a store holding 350
 * and 48 GB sends them looking for a leak that does not exist.
 */
export const RUNS_PAGE_LIMIT = 500;

/** One stored run in full, or undefined when the server has never heard of it. */
async function fetchStoredRun(runId: string): Promise<IStoredRun | undefined> {
    try {
        const res = await fetch(`${location.protocol}//${BackendURI}/simulation/run/${runId}`);
        return res.ok ? await res.json() : undefined;
    } catch { return undefined; }
}

interface IPlantResponse
{
    V: number
}

interface ISimResponse
{
    plants: IPlantResponse[],
    scene: Uint8Array,
    renderer: string,
    debug: string,
    stepTimes: Uint32Array,
    ticksPerMillisecond: number,
    //Only present when the request selected obstacles or the terrain was an imported tray
    //system. The server sends null otherwise.
    obstacleLight?: IObstacleLightDocument | null,
}

interface ISimPreview
{
    scene: Uint8Array,
    renderer: string,
    step: number,
}

/** Mirrors AgroServer/Models/RunSiteResponse.cs. */
interface ISimRunSite
{
    latitude: number,
    longitude: number,
    altitude: number,
    azimuthBase: number,
    azimuthOffset: number,
    timeZone: string, // IANA id of the zone the simulated clock runs on.
    skyCoverage: string, // Cloud cover per timestep, raw little-endian float32, base64 over the wire.
}

interface ITrayModel
{
    panel: number;
    tray: number;
    section: number;
    row: number;
    trayId: string;
    sectionId: string;
    plants: IPlantModel[];
}

export interface IPlantModel
{
    species?: string;
    key?: string;
    type?: string;
    note?: string;
    status?: number;
    x: number;
    y: number;
    z: number;

    speciesName?: string;
    botanicalName? : string;
}

function base64ToArrayBuffer(base64: any) {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
}

export function arrayBufferToBase64(bytes: Uint8Array): string {
    //Chunked because String.fromCharCode.apply throws RangeError past the argument limit, and
    //a per-byte string concatenation of a 60 MB frame is minutes of work.
    const CHUNK = 8192;
    let s = '';
    for (let i = 0; i < bytes.length; i += CHUNK)
        s += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK) as unknown as number[]);
    return btoa(s);
}

/**
 * What one history entry actually costs the heap.
 *
 * The old accounting was `byteLength + 24` while what was retained was the base64 STRING, so the
 * figure in the HUD under-reported reality by 33% - a user watching it pass 1500 Mb was holding
 * about 2.0 GB. Now that the bytes themselves are retained, `byteLength` is the truth; the
 * constant covers the Uint8Array's own object plus its ArrayBuffer, the {t,s} record, and the
 * array slot. V8 charges roughly 96 bytes for a typed array pair even when it wraps nothing.
 */
const HISTORY_ENTRY_OVERHEAD = 136;

function historyEntryBytes(scene: Uint8Array): number {
    return scene.byteLength + HISTORY_ENTRY_OVERHEAD;
}

/**
 * The index of the newest frame actually RECEIVED on the wire, or -1.
 *
 * Not the same as the end of the history any more. The timeline is synced from the run index
 * while the run computes, so it holds entries for steps the server has stored but has not sent -
 * frames with no payload, ahead of the playhead. The transport behaves like a video player
 * buffering: the slider spans what exists, the playhead sits on what has arrived.
 */
let lastReceivedIndex = -1;

/**
 * Files one received frame into the history, by step.
 *
 * A plain `push` was right while the history only ever held frames that had arrived. It stopped
 * being right the moment the timeline started being filled in from the run index during a run: a
 * preview for step 57 would then append a SECOND entry for a step already present, leaving the
 * history non-monotonic with a duplicate `t`. That breaks more than it looks - `isMonotonic` in
 * FrameSource gives up and the whole animation silently falls back to uniform timing, and the
 * slider grows a step that is not a step.
 *
 * Returns where the frame ended up.
 */
export function upsertHistoryFrame(t: number, scene: Uint8Array): number {
    //Sorted by `t`, so a binary search finds the slot whether the frame is new, already known as
    //an index entry, or - after a reconnect - one that arrives twice.
    let lo = 0, hi = st.history.length - 1, at = -1;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        const e = st.history[mid];
        if (e.t === t) { at = mid; break; }
        if (e.t < t) lo = mid + 1; else hi = mid - 1;
    }

    if (at >= 0) {
        const e = st.history[at];
        // A resumed checkpoint can replace this address. The arriving frame owns the slot.
        st.historySize.value += historyEntryBytes(scene) - (e.s ? historyEntryBytes(e.s) : 0);
        st.history[at] = { t, s: scene, plain: true };
        dropCachedScene(at);
        return at;
    }

    const entry = { t, s: scene, plain: true };
    //`lo` is the sorted insertion point, and for a live run it is the end - which is the case
    //worth keeping cheap.
    if (lo >= st.history.length) st.history.push(entry);
    else { st.history.splice(lo, 0, entry); clearSceneCache(); }
    st.historySize.value += historyEntryBytes(scene);
    return lo;
}

/**
 * Frames kept either side of the playhead once eviction starts.
 *
 * Deliberately small and deliberately a COUNT: these are the frames about to be drawn, and
 * re-fetching one mid-playback would stall it. Everything else is the budget's business.
 */
const NEAR_PLAYHEAD = 4;

/**
 * Drops payloads until the history is back inside budget, and stops the run if it cannot get
 * there.
 *
 * The first version kept a fixed window of the newest 64 frames and only considered dropping
 * anything older, which made it a no-op for exactly the runs it exists for. The budget is in
 * BYTES and the window was a COUNT: at the ~108 MB a mature green-facade frame occupies, the
 * budget is crossed around frame 12, so `history.length - 64` clamped to zero and the loop body
 * never executed. Nothing was dropped, nothing was reported, and because a run id was present the
 * `stopForMemory` fallback was skipped too - so the one path that could have saved the tab was
 * disabled by the presence of the thing meant to make dropping safe.
 *
 * Both halves of that are fixed here: the window is byte-driven, and failing to reach the target
 * escalates instead of returning quietly.
 */
export function evictOldestPayloads() {
    const budget = st.historyBudget.peek();
    const target = budget * 0.75;
    const pointer = st.playPointer.peek();
    const last = st.history.length - 1;

    //Two passes, because the comfort window is a luxury and the budget is not.
    //
    //The first pass spares the newest frame and the frames either side of the playhead: those are
    //about to be drawn, and re-fetching one mid-playback stalls it. That is worth a few hundred
    //megabytes when there are a few hundred megabytes to spare.
    //
    //When there are not, the second pass takes them too. It used to stop the run instead, on the
    //reasoning that a handful of frames exceeding the budget leaves nothing to do - but that was
    //written for a client that was the only copy. With a store every one of those frames is on the
    //server and comes back in about three milliseconds, so refusing to drop them buys a smoother
    //scrub at the price of the rest of the simulation. Measured on a mature stand, ten retained
    //frames are hundreds of megabytes against a budget under two gigabytes: the run stopped at
    //simulated month 4 of 10 with 16 GB safely on disk and nothing wrong except this rule.
    const evict = (spareWindow: boolean) => {
        let dropped = 0;
        for (let i = 0; i <= last && st.historySize.peek() > target; ++i) {
            if (spareWindow && (i === last || Math.abs(i - pointer) <= NEAR_PLAYHEAD)) continue;
            //The frame on screen is the one exception that survives both passes: dropping it would
            //blank the viewport until a round trip completed, and it is a single frame.
            if (!spareWindow && i === pointer) continue;

            const e = st.history[i];
            if (!e || !e.s) continue;
            st.historySize.value -= historyEntryBytes(e.s);
            e.s = undefined;
            ++dropped;
        }
        return dropped;
    };

    let dropped = evict(true);
    //Only when a store can serve them back. Without one a dropped frame is gone, and the old
    //behaviour - stop the run, keep what was computed - remains the right trade.
    const recoverable = !!st.runId.peek();
    if (st.historySize.peek() > budget && recoverable) dropped += evict(false);

    if (dropped > 0) st.evictedFrames.value += dropped;

    //Still over after dropping everything droppable. With a store that means ONE frame - the one
    //being displayed - exceeds the whole budget, and no amount of eviction will help; without one
    //it means the frames worth keeping do. Either way the tab cannot hold what is arriving.
    if (st.historySize.peek() > budget) st.stopForMemory();
}

/**
 * Hands every resident frame payload back to the server, which already has them.
 *
 * Eviction does this under pressure, one budget's worth at a time. This does it deliberately, and
 * it exists because of the arithmetic at production scale: one 7,700-plant frame on screen costs
 * about 1.1 GB of heap all by itself, so a tab that has just finished a long run is already near
 * its ceiling before an export allocates anything. The frames are on disk; the export reads them
 * one at a time; holding the whole run in memory as well buys nothing and is what puts the tab
 * over the edge.
 *
 * Does nothing without a run id. Then the payloads really are the only copy.
 *
 * Returns how many were released.
 */
export function releaseHeldFrames(keepAround = -1): number {
    if (!st.runId.peek()) return 0;
    let dropped = 0;
    for (let i = 0; i < st.history.length; ++i) {
        //A small window is kept around a frame the caller says it is about to need, so releasing
        //does not immediately cost a round trip for the frame on screen.
        if (keepAround >= 0 && Math.abs(i - keepAround) <= NEAR_PLAYHEAD) continue;
        const e = st.history[i];
        if (!e || !e.s) continue;
        st.historySize.value -= historyEntryBytes(e.s);
        e.s = undefined;
        ++dropped;
    }
    if (dropped > 0) st.evictedFrames.value += dropped;
    return dropped;
}

/**
 * Rehydrates a saved session's history.
 *
 * The on-disk format keeps base64, so sessions saved before the in-memory switch to bytes still
 * load. Entries are converted in place and the strings dropped as we go, so loading a large
 * session does not hold both representations at once.
 */
export function decodeSavedHistory(saved: unknown): { t: number, s: Uint8Array | undefined, plain: boolean }[] {
    if (!Array.isArray(saved)) return [];
    const out: { t: number, s: Uint8Array | undefined, plain: boolean }[] = new Array(saved.length);
    for (let i = 0; i < saved.length; ++i) {
        const e = saved[i];
        out[i] = { t: e.t, s: typeof e.s === 'string' ? base64ToArrayBuffer(e.s) : e.s, plain: true };
        saved[i] = undefined;
    }
    return out;
}

/**
 * What a history actually occupies, counted from the entries themselves.
 *
 * Legacy JSON holds raw bytes; a binary archive leaves frames on disk until requested.
 * Count the resident payloads rather than trusting a size recorded by a different storage mode.
 */
export function measureHistoryBytes(history: { s: Uint8Array | undefined }[]): number {
    let total = 0;
    for (const e of history)
        if (e && e.s) total += historyEntryBytes(e.s);
    return total;
}

/**
 * How much history to hold before refusing more.
 *
 * Derived from the tab's real ceiling where the browser exposes it. The fraction is deliberately
 * well under 1: the decoded scene, the instance list, the GPU staging buffers and a transient
 * spike of several times the frame size all have to fit in the same heap, and the frame arriving
 * when the limit is reached is itself the largest one so far.
 */
const HISTORY_BUDGET_FRACTION = 0.45;

/**
 * The same budget once the frames also exist on the server.
 *
 * 0.45 was chosen for a client that was the only place the history existed: dropping a frame lost
 * it, so the only alternative to holding on was stopping the run, and holding on was worth
 * pushing. With a run store neither is true - a dropped frame comes back in about three
 * milliseconds - so holding 1.9 GB of recoverable bytes is simply spending the headroom that the
 * rest of the tab needs.
 *
 * And it needs a lot. Measured at 7,700 plants (`tools/visual/heapcheck.mjs`), ONE frame on
 * screen costs 1,148 MB: about 461 MB for the decoded scene, 579 MB in renderer structures that
 * never come back, and only 108 MB - nine per cent - for the payload the budget actually governs.
 * A budget that ignores the other 91 % is not a ceiling, and that is why the tab still died.
 */
const HISTORY_BUDGET_FRACTION_WITH_STORE = 0.15;
const HISTORY_BUDGET_FALLBACK = 1_200_000_000;

function historyBudgetBytes(hasStore: boolean): number {
    const fraction = hasStore ? HISTORY_BUDGET_FRACTION_WITH_STORE : HISTORY_BUDGET_FRACTION;
    const limit = (performance as any)?.memory?.jsHeapSizeLimit;
    return typeof limit === 'number' && limit > 0
        ? limit * fraction
        : HISTORY_BUDGET_FALLBACK * (fraction / HISTORY_BUDGET_FRACTION);
}

/** Re-reads the budget for the current storage situation. */
function refreshHistoryBudget() {
    st.historyBudget.value = historyBudgetBytes(!!st.runId.peek());
}

function exportObstacle(o: Obstacle) {
    const base = { T: o.type.peek(), H: o.height.peek(), D: o.thickness.peek(),
                    P: { X: o.px.peek(), Y: o.py.peek(), Z: o.pz.peek() },
                    O: o.angleY.peek() };
    switch (o.type.peek()) {
        case "umbrella": return { ...base, R: o.wallLength_UmbrellaRadius.peek() };
        default: return { ...base, L: o.wallLength_UmbrellaRadius.peek() };
    }
}

export enum PlayState {
    None, //noop
    Backward, //playing backward
    Forward, //playing forward
    ForwardWaiting, //playing forward and waiting for new frames to come
    SeekBackward, //jump to the first frame
    SeekForward, //jump to the last frame
    NextBackward, //one frame backward
    NextForward, //one frame forward
    Seek //jump to the specified frame
}

class State {
    // SETTINGS
    hoursPerTick = signal(4);
    totalHours = signal(1440);
    fieldResolution = signal(0.05);
    fieldSizeX = signal(10);
    fieldSizeZ = signal(10);
    fieldSizeD = signal(4);
    fieldCellsX = computed(() => Math.ceil(this.fieldSizeX.value / this.fieldResolution.value));
    fieldCellsZ = computed(() => Math.ceil(this.fieldSizeZ.value / this.fieldResolution.value));
    fieldCellsD = computed(() => Math.ceil(this.fieldSizeD.value / this.fieldResolution.value));
    terrainList: ITerrainItem[] = [];
    terrainTimestamp = signal(Date.now());
    initNumber = signal(42);
    randomize = signal(false);
    renderMode = signal(3); //3 is Internal CPU

    /**
     * Substring that selects which obstacles get a leaf area index and a solar load, e.g. "Wand"
     * for the walls of an imported building. Empty measures nothing, which is the default because
     * the measurement costs rays on every simulation step.
     *
     * Only the internal CPU light model measures - render mode 3, or 1 and 2 once their renderer
     * fails to answer and they fall back to it.
     */
    leafAreaIndexObstacles = signal("");
    /** Measurement points spread over the selected obstacles, in proportion to area. */
    leafAreaIndexSensors = signal(32768);
    /** How far in front of a surface foliage still counts towards its leaf area index, metres. */
    leafAreaIndexDepth = signal(3);
    /** Height of the bands each obstacle is resolved into, metres. */
    leafAreaIndexBandHeight = signal(0.5);

    //RESPONSE of the leaf area index measurement: the whole document, budgeted. Arrives once,
    //with the finished run.
    obstacleLight = signal<IObstacleLightDocument | undefined>(undefined);
    /** Which measured quantity, if any, colours the obstacles in the viewport. */
    obstacleMapping = signal<ObstacleMappingOptions>(ObstacleMappingOptions.None);

    /** Local date the viewport's current step falls on, or undefined before a run has data. */
    shownObstacleDate = computed<string | undefined>(() => {
        const meta = this.obstacleLight.value?.meta;
        if (!meta) return undefined;
        //playPointer.value is the subscription and timestepAt turns it into the simulated step;
        //passing the value in rather than calling currentTimestep() is deliberate, see timestepAt.
        return stepDate(meta.initialDate, meta.hoursPerTick, this.timestepAt(this.playPointer.value));
    });

    /**
     * Obstacle name to measured value for the day the viewport is showing, or undefined when
     * nothing is mapped. Recomputed only when the mapping, the data or the shown day changes -
     * the renderer rebuilds vertex colours from it, so it must not churn per frame.
     */
    obstacleMappingValues = computed<Map<string, number> | undefined>(() => {
        const mapping = this.obstacleMapping.value;
        const doc = this.obstacleLight.value;
        if (mapping === ObstacleMappingOptions.None || !doc || doc.obstacles.length === 0)
            return undefined;

        const date = this.shownObstacleDate.value;
        const values = new Map<string, number>();
        for (const item of doc.obstacles) {
            const value = obstacleValueOn(doc, item, mapping, date);
            if (value !== undefined)
                values.set(item.name, value);
        }
        return values;
    });

    /**
     * Value range the obstacle colours span. Fixed by the mapping where the quantity has a natural
     * scale, so facades stay comparable between runs, and taken from the data only where it does
     * not - avoided irradiation grows with the length of the run.
     */
    obstacleMappingRange = computed<[number, number]>(() => {
        const mapping = this.obstacleMapping.value;
        if (mapping === ObstacleMappingOptions.None)
            return [0, 1];
        const spec = obstacleMappingSpec[mapping];
        if (spec.max !== undefined)
            return [spec.min, spec.max];
        let max = 0;
        const values = this.obstacleMappingValues.value;
        if (values)
            for (const v of values.values())
                max = Math.max(max, v);
        return [spec.min, max > 0 ? max : 1];
    });
    exactPreview = signal(false);
    /**
     * Calendar date the simulation starts on, as YYYY-MM-DD.
     *
     * The frontend never used to send this, so the backend silently fell back to its own
     * default of 2026-02-01 - a very low sun at 48°N, which makes light-driven growth behave
     * quite differently from what a summer start would give. See Agro/AgroWorld.cs.
     */
    initialDate = signal(DEFAULT_INITIAL_DATE);

    /**
     * Where on Earth the world origin sits, and how the scene is turned on the compass.
     *
     * These drive the sun, and through it the shadows, the light the plants receive and the
     * sky. Until now they were compile-time constants on both sides; they are settings, so a
     * run can be sited somewhere other than Vienna.
     */
    latitude = signal(LATITUDE);
    longitude = signal(LONGITUDE);
    /** Metres above sea level. Thins the atmosphere the beam crosses; see SkyDome. */
    altitude = signal(ALTITUDE);
    /**
     * Scene rotation about the up axis before the terrain's own facing is accounted for,
     * degrees. 0 puts the scene's +X (its right vector) due east once the terrain's own
     * compensation is added, and an increasing value turns it clockwise seen from above.
     *
     * It rotates the SUN, not the geometry: it states how the scene's axes are oriented on the
     * compass, and nothing in the scene graph moves.
     */
    azimuthBase = signal(AZIMUTH);
    /**
     * Which way the loaded terrain model faces, as an index into AZIMUTH_OFFSETS. Backward
     * (-Z) is the basic case and needs no compensation; Forward (+Z), the usual case, needs
     * half a turn - see AZIMUTH_OFFSETS for the rest.
     */
    azimuthOffset = signal(0);
    /**
     * The azimuth the sun is actually computed against: azimuthBase plus the compensation for
     * azimuthOffset, wrapped into [0, 360). This is what every renderer reads.
     */
    azimuth = computed(() => {
        const offset = AZIMUTH_OFFSETS[this.azimuthOffset.value] ?? AZIMUTH_OFFSETS[0];
        return ((this.azimuthBase.value + offset.degrees) % 360 + 360) % 360;
    });
    /**
     * The IANA zone the SERVER derived from the coordinates, once it has told us.
     *
     * Undefined until then, and the fallback is the default site's zone. No browser API maps
     * coordinates to a zone, so this cannot be computed here - and guessing is not harmless:
     * the wrong zone shifts every sun position and the whole sky by its offset while still
     * looking completely plausible.
     */
    siteTimeZone = signal<string | undefined>(undefined);

    //SPECIES
    species = signal<Species[]>([Species.Default()]);
    behaviors = signal<string[]>([]);

    //INITIAL SCENE SETUP
    seedsPerField = signal(1);
    seedsOptimalDistance = signal(0.165);
    seeds = signal<Seed[]>([]);
    seedsCount = computed(() => this.seeds.value.length);

    obstacles = signal<Obstacle[]>([]);
    obstaclesCount = computed(() => this.obstacles.value.length);

    //RENDERING
    //The scene graph belongs to whichever renderer is mounted; appstate holds only the data
    //it is built from. See src/render/IViewportRenderer.ts.
    viewportBackend = signal<"auto" | "three" | "webgpu">("auto");
    /**
     * Which HUD tab is showing. Lifted out of app.tsx because the viewport needs it: the
     * transform gizmo is a Three.js control with no WebGPU equivalent, so editing tabs
     * mount the Three backend regardless of preference.
     */
    activeTab = signal("tab-home");

    /**
     * Viewport lighting quality. Purely visual - it never reaches the simulation.
     *
     * Deliberately separate from `renderMode`, which selects the SERVER's light-transport
     * engine and changes the irradiance the plants actually consume.
     */
    //Independent switches rather than a ladder; see LightingOptions. All off is the parity
    //look, which is what keeps the default byte-identical to the WebGL renderer.
    //Physical daylight: a modest sky plus a dominant sun, instead of the historical flat
    //ambient that makes occlusion and indirect light invisible. See LightingOptions.
    lightDaylight = signal(false);
    lightShadows = signal(false);
    lightOcclusion = signal(false);
    lightIndirect = signal(false);
    lightSecondBounce = signal(false);
    //Leaves pass light through to their shaded face. Off by default because, unlike the other
    //switches, the term it adds is live with no sun at all - it modulates the hemisphere
    //ambient, which is the only light the viewport has until something else is ticked.
    lightLeafTranslucency = signal(false);
    //Normal-offset shadow bias in shadow texels. Tunable because the right value depends on
    //the canopy: too little and a leaf shadows itself, too much and light leaks between
    //leaves that genuinely touch.
    shadowBiasTexels = signal(SHADOW_NORMAL_OFFSET_TEXELS);
    /**
     * The physical sky dome as the viewport background, instead of the flat theme colour.
     *
     * Deliberately NOT part of lightingOptions(). It adds no light - it only replaces what is
     * behind the scene - and LightingOptions feeds resolveLighting, whose result shape at the
     * parity setting is an asserted contract. Keeping it out means no churn in that contract
     * and no field resolveLighting has to be told to ignore.
     */
    lightSky = signal(false);
    /**
     * Cloud cover per timestep, 0 = clear .. 1 = fully overcast: the server's
     * Weather[t].SkyCoverage, which is planned in full before step 0 and sent once for the run.
     *
     * Undefined for a run from a server that predates the field, or an archive saved before it.
     * That is a documented fallback to a CLEAR sky, not an error - see skyCoverageAt.
     */
    weather = signal<Float32Array | undefined>(undefined);

    /**
     * The site, peeked. For the non-reactive callers - the mount catch-up and the offline
     * renderer. Anything inside a signal effect must read the signals itself and pass them in,
     * or the effect never re-runs when the site moves.
     */
    solarSite(): SolarSite {
        return {
            latitude: this.latitude.peek(),
            longitude: this.longitude.peek(),
            altitudeM: this.altitude.peek(),
            azimuthDeg: this.azimuth.peek(),
            timeZone: this.siteTimeZone.peek(),
        };
    }

    lightingOptions(): LightingOptions {
        return {
            daylight: this.lightDaylight.peek(),
            shadows: this.lightShadows.peek(),
            occlusion: this.lightOcclusion.peek(),
            indirect: this.lightIndirect.peek(),
            secondBounce: this.lightSecondBounce.peek(),
            leafTranslucency: this.lightLeafTranslucency.peek(),
            shadowBiasTexels: this.shadowBiasTexels.peek(),
        };
    }
    //Diagnostic overlay for the voxel GI's density field. Not persisted and not sent to the
    //server: it shows a rendering intermediate, not anything the simulation knows about.
    showVoxels = signal(false);
    voxelMip = signal(0);
    voxelGain = signal(1);
    voxelAlbedo = signal(false);
    activeBackend = signal<"three" | "webgpu" | undefined>(undefined);

    /**
     * Set while the offline animation renderer is stepping through history.
     *
     * A plain field, NOT a signal: writing it must not trigger any effect, and its only job
     * is to suppress the playback/preview poke that normally follows a plant rebuild.
     */
    offlineRendering = false;

    needsRender = signal(false);
    visualMapping = signal(VisualMappingOptions.Natural);
    plantPick = signal("");
    seedPick = signal(-1);
    terrainPick = signal(-1);

    downloadRoots = signal(false);
    debugBoxes = signal(false);
    showLeaves = signal(true);
    showTerrain = signal(true);
    showRoots = signal(true);
    showObstacles = signal(true);
    showSeeds = signal(true);

    performPicking = signal(true);

    grabbed = computed(() => this.seeds.value.filter((x: Seed) => x.state.value == "grab"));

    //OPERATIONAL STATE
    computing = signal(false);
    playing = signal(PlayState.None);
    playPointer = signal(0);
    playRequest = signal(false);
    previewRequest = signal(false);
    previewRequestAfterSceneUpdate = false;
    simStep = signal(0);
    simLength = signal(0);

    //RESPONSE
    plants = signal<IPlantResponse[]>([]);
    scene = signal<PlantModel[]>([]);
    renderer = signal("");
    samplesPerPixel = signal(2048);
    simHoursPerTick = 1;

    /**
     * The simulation timestep the viewport is currently showing.
     *
     * NOT the same as playPointer: that is an index into `history`, and previews can be
     * skipped, so the two diverge as soon as the backend drops a frame. Using the pointer to
     * drive the clock puts the sun at the wrong time of day - which reads as broken lighting
     * rather than as the units mistake it is.
     */
    currentTimestep(): number {
        return this.timestepAt(this.playPointer.peek());
    }

    /**
     * The simulated timestep a playhead position refers to.
     *
     * Takes the pointer as an argument so a caller inside a signal effect can pass
     * `playPointer.value` and actually subscribe to it. The obvious spelling - reading the
     * signal for its subscription and calling `currentTimestep()` for the value - was written
     * as `(playPointer.value, currentTimestep())`, and terser deletes the discarded operand of
     * a comma expression as dead code. The effect then never re-ran as the animation played,
     * so the sun stayed frozen at whatever time it was when the tier last changed.
     */
    timestepAt(pointer: number): number {
        const i = Math.max(0, Math.min(pointer, this.history.length - 1));
        return this.history[i]?.t ?? 0;
    }

    /**
     * One entry per received timestep.
     *
     * `s` is the V7 scene. It arrives raw and is deflated in the background once it falls out of
     * the hot window, so `plain` says which it currently is - roughly 1.7x on real scene data,
     * which is what float-heavy payloads give.
     */
    history : {t: number, s: Uint8Array | undefined, plain: boolean}[] = [];
    historySize = signal(0);

    /**
     * How many frames the history holds, as a signal.
     *
     * `historySize` used to carry this too: the transport bar asked `historySize.value == 0` to
     * mean "there is nothing to play", and the export panel read it purely to subscribe. That
     * conflation held only while every frame in the history had its payload. It stops being true
     * the moment frames are evicted to the server or a reload rebuilds the timeline from the run
     * index - a history of 600 frames then legitimately occupies zero bytes, and every transport
     * control would be disabled over a run that is entirely intact on disk.
     *
     * Bytes and frames are two different questions. This one answers "how many frames".
     */
    historyCount = signal(0);
    /** Set when a run was stopped because the history filled the heap budget. */
    historyLimitHit = signal(false);
    /** The ceiling `historySize` is measured against, so the HUD can show a share, not a number. */
    historyBudget = signal(historyBudgetBytes(false));
    /**
     * The server-side store backing this run, when there is one.
     *
     * Its presence is what makes dropping a frame from memory safe rather than lossy: with a run
     * id the payload can be fetched back, without one it is gone for good.
     */
    runId = signal('');

    //--- the archive ---------------------------------------------------------------------
    /** Stored runs as the server last reported them. Empty until something asks for them. */
    runs = signal<IStoredRun[]>([]);
    runsLoading = signal(false);
    /** The last thing that went wrong in the runs panel, for the panel to show. */
    runsError = signal('');
    /** A run id while an action on it is in flight, so its row can disable its own buttons. */
    runsBusy = signal('');
    /** The stored run currently open for viewing, as opposed to one this tab is computing. */
    viewingRunId = signal('');
    /**
     * The stored run the NEXT run will be recorded as derived from.
     *
     * Lineage only. Nothing resumes: there is no checkpoint of live world state anywhere in this
     * project, so a continued or forked run is computed again from the recorded request. That
     * reproduces the parent exactly only when the parent was deterministic, which is why the flag
     * is stored per run and shown in the panel.
     */
    lineageParentRunId = signal('');
    /**
     * The stored run whose field model the next run should be built from.
     *
     * Held separately from the lineage because they come apart: a user can fork a run, upload a
     * different facade, and still want the lineage recorded. An upload clears this; nothing else
     * does, so a fork can be re-run repeatedly without the 94 MB model being sent again.
     */
    fieldModelRunId = signal('');

    /**
     * Run sequentially so the result can be reproduced.
     *
     * Off by default because it costs throughput. Measured: with several plants the same request
     * and seed diverges at the FIRST timestep on the parallel path - 59 of 60 frames differed -
     * so a branched scenario could never be compared against the run it came from.
     */
    deterministic = signal(false);
    /**
     * Which contact engine the server runs: 0 the certified engine (growth is certified
     * non-penetrating and suspended otherwise), 1 the soft single-pass engine (contacts bend and
     * fold but never block growth, obstacles included), 2 the mixed engine (soft foliage, hard
     * obstacles and soil), the default. Mirrors `ContactEngineKind` on the server; they are meant
     * to be compared on the same request. See Agro/SOFT_CONTACTS.md. A stored run that predates
     * the field ran under the certified engine, hence the `?? 0` when one is loaded.
     */
    contactEngine = signal(2);
    /** Frames whose payload has been dropped and would have to be re-fetched. */
    evictedFrames = signal(0);

    /**
     * Stops the run because there is no room left to keep its results.
     *
     * Aborting is the point: the frames already received stay valid and saveable, whereas letting
     * the run continue ends in the tab being killed and everything lost. Latched so the abort is
     * sent once, not once per remaining frame.
     */
    stopForMemory() {
        if (this.historyLimitHit.peek()) return;
        this.historyLimitHit.value = true;
        console.warn(
            `Simulation stopped: the stored history reached ` +
            `${(this.historySize.peek() / 1048576).toFixed(0)} MB, the limit this browser tab can hold. ` +
            `The frames computed so far are intact and can be saved or exported.`);
        if (this.computing.peek()) hubConnection.invoke("abort");
    }

    fieldModelPath = signal("");
    fieldItemRegex = signal("erde");
    fieldItemRegexMaterial = signal(true);
    fieldModelData?: string = undefined;
    modelParsingProgress = signal(0);
    seedsDistributionData? = "";
    seedsDistributionPath = signal("");

    //METHODS
    private requestBody = () => {
        return {
        HoursPerTick: Math.trunc(this.hoursPerTick.peek()),
        TotalHours: Math.trunc(this.totalHours.peek()),
        FieldResolution: this.fieldResolution.peek(),
        FieldSize: { X: this.fieldSizeX.peek(), D: this.fieldSizeD.peek(), Z: this.fieldSizeZ.peek() },
        Seed: Math.trunc(this.randomize.peek() ? Math.random() * 4294967295 : this.initNumber.peek()),
        InitialDate: this.initialDate.peek(),
        Latitude: this.latitude.peek(),
        Longitude: this.longitude.peek(),
        Altitude: this.altitude.peek(),
        AzimuthBase: this.azimuthBase.peek(),
        AzimuthOffset: this.azimuthOffset.peek(),
        Species: this.species.peek().map(s => s.serialize()),

        Plants: this.seeds.peek().map((p: Seed) => ({ S: p.species.peek(), P: { X: p.px.peek(), Y: p.py.peek(), Z: p.pz.peek() }, F: p.fieldIndex.peek() })),
        Obstacles: this.obstacles.peek().map((o: Obstacle) => exportObstacle(o)),
        RequestGeometry: true,
        RenderMode: this.renderMode.value,
        SamplesPerPixel: this.samplesPerPixel.value,

        LeafAreaIndexObstacles: this.leafAreaIndexObstacles.value.trim().length > 0 ? this.leafAreaIndexObstacles.value.trim() : null,
        LeafAreaIndexSensors: Math.trunc(this.leafAreaIndexSensors.value),
        LeafAreaIndexDepth: this.leafAreaIndexDepth.value,
        LeafAreaIndexBandHeight: this.leafAreaIndexBandHeight.value,
        ExactPreview: this.exactPreview.value,
        DownloadRoots: this.downloadRoots.value,

        FieldItemRegex: this.fieldItemRegex.value,
        Deterministic: this.deterministic.value,
        ContactEngine: this.contactEngine.value,
        FieldItemRegexMaterial: this.fieldItemRegexMaterial.value,
        FieldModelPath: this.fieldModelPath.value,
        //Lineage, and where to get the terrain from when this tab has not uploaded one. Null
        //rather than "" - the server treats an empty string as a run id and looks it up.
        ParentRunId: this.lineageParentRunId.value || null,
        FieldModelRunId: this.fieldModelRunId.value || null,
        //FieldModelData is deliberately NOT sent. `SimulationRequest` has no such member (only
        //`FieldModelKey`, which nothing sets), so System.Text.Json discarded it silently - while
        //every run JSON-stringified, escaped and uploaded the whole facade OBJ again for it.
        //On the 93.7 MB green-facade model that was ~98 MB of upload per run, for nothing. The
        //model reaches the simulation through /simulation/terrain and ClientTerrains instead.
    }};

    run = async() => {
        if (this.computing.peek())
            hubConnection.invoke("abort");
        else
        {
            this.simHoursPerTick = this.hoursPerTick.peek();
            //console.log(this.scene);
            batch(() => {
                this.computing.value = true;
                this.playing.value = PlayState.ForwardWaiting;
                this.playPointer.value = 0;
                this.renderer.value = "";
                this.historySize.value = 0;
                this.historyCount.value = 0;
                lastReceivedIndex = -1;
                this.historyLimitHit.value = false;
                this.runId.value = '';
                //A new run's measurement has not arrived yet; leaving the old one would colour
                //the obstacles by numbers that belong to a scene that no longer exists.
                this.obstacleLight.value = undefined;
                this.evictedFrames.value = 0;
            });
            refreshHistoryBudget();
            //The stored id has to go at the same moment the signal does. A reload in the window
            //between asking for a run and `runStarted` arriving would otherwise resubscribe to
            //the PREVIOUS run and stream its frames into this one's empty history.
            try { sessionStorage.removeItem('agro.runId'); } catch { /* private mode */ }
            this.history.length = 0;
            this.archiveMeasurement = undefined;
            clearSceneCache();
            resetCompression();
            if (hubConnection.state !== SignalR.HubConnectionState.Connected)
                await start();

            if (hubConnection.state == SignalR.HubConnectionState.Connected)
            {
                console.log(this.requestBody());
                const prepared = await fetch(`${location.protocol}//${BackendURI}/simulation/upload`, {
                    body: JSON.stringify(this.requestBody()),
                    method: 'post',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json'
                    }
                });

                const preparedID = await prepared.text();
                if (preparedID?.length > 0)
                    hubConnection.invoke("start", preparedID).catch(e => {
                        console.error(e);
                        batch(() => {
                            this.computing.value = false;
                            this.renderer.value = "error";
                        });
                    });
                // hubConnection.invoke("run", this.requestBody()).catch(e => {
                //     console.error(e);
                //     batch(() => {
                //         this.computing.value = false;
                //         this.renderer.value = "error";
                //     });
                // });
                this.previewRequest.value = true;
            }
            else
                this.computing.value = false;
        }
    }

    play = async(state: PlayState) => {
        if (state != PlayState.Forward || this.playPointer.peek() < this.history.length - 1)
            this.playing.value = state;
        else
            this.playing.value = PlayState.ForwardWaiting;
    }

    clearSeeds = (fieldIndex = -1) => {
        if (fieldIndex >= 0)
        {
            const remove : number[] = [];
            const keep : Seed[] = [];
            const src = this.seeds.peek();
            src.forEach((s, i) => {
                if (s.fieldIndex.peek() == fieldIndex)
                    remove.push(i);
                else
                    keep.push(s);
            });
            this.seeds.value = keep;
        }
        else
            this.seeds.value = [ ];
        this.needsRender.value = true;
    }

    batchTerrainsClear(pattern: string) {
        if (pattern?.length > 0 && this.terrainList?.length > 0)
        {
            const fieldIndexesToRemove = new Set<number>();
            for(let i = 0; i < this.terrainList.length; ++i)
                if (this.terrainList[i].id?.match(pattern))
                    fieldIndexesToRemove.add(i);

            const remove : number[] = [];
            const keep : Seed[] = [];
            const src = this.seeds.peek();
            src.forEach((s, i) => {
                if (fieldIndexesToRemove.has(s.fieldIndex.peek()))
                    remove.push(i);
                else
                    keep.push(s);
            });
            this.seeds.value = keep;
            this.needsRender.value = true;
        }
    }

    pushRndSeed = (count?: number) => {
        const species = this.species.peek().filter(s => s.includeInRndGen.value);
        if (species.length == 0)
            console.error("No species selected for random seeding");
        else if (count)
        {
            if (count >= 1)
            {
                const result : Seed[] = [];
                if (this.terrainList?.length > 0)
                {
                    for(let t = 0; t < this.terrainList.length; ++t)
                        for(let i = 0; i < count; ++i)
                            result.push(Seed.rndItem(species, 0, t));
                }
                else
                {
                    for(let i = 0; i < count; ++i)
                        result.push(Seed.rndItem(species));
                }
                this.seeds.value = [ ...this.seeds.peek(), ...result]
            }
            else
                this.seeds.value = [ ...this.seeds.peek(), Seed.rndItem(species)]
        }
    };

    pushSeedRaster = (dist: number) => {
        const result : Seed[] = [];
        const species = this.species.peek().filter(s => s.includeInRndGen.value);
        if (species.length == 0)
            console.error("No species selected for random seeding");
        else
            batch(() => {
                if (this.terrainList?.length > 0)
                {
                    for(let i = 0; i < this.terrainList.length; ++i)
                    {
                        const terrain = this.terrainList[i];

                        if (terrain instanceof MeshTerrainItem)
                        {
                            const principatDir = terrain.principalDir;
                            const secondaryDir = terrain.secondaryDir;
                            const sx = terrain.principalSize;
                            const sz = terrain.secondarySize;
                            const xCount = Math.max(1, Math.round(sx / dist));
                            const zCount = Math.max(1, Math.round(sz / dist));
                            const xDist = sx / xCount;
                            const zDist = sz / zCount;
                            const xStart = xDist * 0.5;
                            const start = addScaled2(vec2(principatDir.x * xStart, principatDir.y * xStart), secondaryDir, zDist * 0.5);
                            for(let x = 0; x < xCount; ++x)
                            {
                                const mx = x * xDist;
                                for(let z = 0; z < zCount; ++z)
                                {
                                    const stepped = addScaled2(vec2(principatDir.x * mx, principatDir.y * mx), secondaryDir, z * zDist);
                                    const pos = { x: stepped.x + start.x, y: stepped.y + start.y };
                                    result.push(new Seed(species[Math.floor(Math.random() * species.length)].name.peek(), pos.x, Math.max(0, terrain.sy() - 0.02), pos.y, i, false));
                                }
                            }
                        }
                        else
                        {
                            const sx = terrain.sx();
                            const sz = terrain.sz();
                            const xCount = Math.max(1, Math.round(sx / dist));
                            const zCount = Math.max(1, Math.round(sz / dist));
                            const xDist = sx / xCount;
                            const zDist = sz / zCount;
                            const xStart = xDist * 0.5;
                            const zStart = zDist * 0.5;
                            for(let x = 0; x < xCount; ++x)
                                for(let z = 0; z < zCount; ++z)
                                    result.push(new Seed(species[Math.floor(Math.random() * species.length)].name.peek(), xStart + x * xDist, -0.02, zStart + z * zDist, i, false));
                        }
                    }
                }
                else
                {
                    const xCount = Math.max(1, Math.round(this.fieldSizeX.value / dist));
                    const zCount = Math.max(1, Math.round(this.fieldSizeZ.value / dist));
                    const xDist = this.fieldSizeX.value / xCount;
                    const zDist = this.fieldSizeZ.value / zCount;
                    const xHalf = xDist * 0.5;
                    const zHalf = zDist * 0.5;
                    for(let x = 0; x < xCount; ++x)
                        for(let z = 0; z < zCount; ++z)
                            result.push(new Seed(species[Math.floor(Math.random() * species.length)].name.peek(), xHalf + x * xDist, -0.02, zHalf + z * zDist, 0, false));
                }
                this.seeds.value = [ ...this.seeds.value, ...result];
            });
    }

    removeSeedAt = (i : number) => {
        const seeds = this.seeds.peek();
        if (i >= 0 && i < seeds.length)
            seeds.splice(i, 1);
        this.seeds.value = [...seeds];
        this.needsRender.value = true;
    };

    pushRndSpecies = () => {
        this.species.value = [ ...this.species.peek(), new Species()]
    };

    removeSpeciesAt = (i : number) => {
        const species = this.species.peek();
        if (i >= 0 && i < species.length && species.length > 1) {
            //The [0] this used to end with was vestigial - copied from removeSeedAt, which
            //actually needed the removed element. splice does the work.
            species.splice(i, 1);
        }
        this.species.value = [...species];
    };

    clearHovers = (src: BaseRequestObject[], except: BaseRequestObject | undefined) => {
        src.forEach((x : BaseRequestObject) => {
            if (x !== except)
            {
                switch (x.state.peek()) {
                    case "hover": x.unhover(); break;
                    case "selecthover": x.select(); break;
                }
            }
        });
    };

    clearSeedHovers = (except: Seed | undefined) => this.clearHovers(this.seeds.peek(), except);
    clearObstacleHovers = (except: Obstacle | undefined) => this.clearHovers(this.obstacles.peek(), except);
    clearTerrainHovers = (except: ITerrainItem | undefined) => this.clearHovers(this.terrainList, except);

    clearSelects = (src: BaseRequestObject[], except: Seed | Obstacle | ITerrainItem | undefined) => {
        src.forEach((s : BaseRequestObject) => {
            if (s !== except)
                switch(s.state.peek()) {
                    case "select":
                    case "selecthover": s.unhover();
                }
        });
    };

    clearSeedSelects = (except: Seed | undefined) => this.clearSelects(this.seeds.peek(), except);
    clearObstacleSelects = (except: Obstacle | undefined) => this.clearSelects(this.obstacles.peek(), except);
    clearTerrainSelects = (except: ITerrainItem | undefined) => this.clearSelects(this.terrainList, except);

    clearGrabs = (src: BaseRequestObject[], except: Seed | Obstacle | undefined) => {
        src.forEach((s : BaseRequestObject) => {
            if (s !== except)
                if (s.state.peek() == "grab")
                    s.ungrab("select");
        });
    };

    clearSeedGrabs = (except: Seed | undefined) => this.clearGrabs(this.seeds.peek(), except);
    clearObstacleGrabs = (except: Obstacle | undefined) => this.clearGrabs(this.obstacles.peek(), except);

    pushRndObstacle = () => { this.obstacles.value = [ ...this.obstacles.peek(), Obstacle.rndObstacle()] };

    removeObstacleAt = (i : number) => {
        const obstacles = this.obstacles.peek();
        if (i >= 0 && i < obstacles.length)
            obstacles.splice(i, 1);

        this.obstacles.value = [...obstacles];
        this.needsRender.value = true;
    };

    archiveMeasurement: Blob | undefined;

    save = async () => {
        try { await this.saveArchive(); }
        catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') return;
            console.error('Could not save run:', error);
            alert(`Could not save the complete run: ${error instanceof Error ? error.message : String(error)}`);
        }
    };

    private saveArchive = () => saveRunArchive(async () => {
        const runId = this.runId.peek();
        if (runId && await syncTimelineToIndex(runId, false) === 0 && this.history.length > 0)
            throw new Error('Could not verify the saved run index. Please retry the save.');
        if (runId !== this.runId.peek()) throw new Error('The selected run changed while saving. Please retry.');
        const revision = frameRevisions.get(runId) ?? '0';
        const data = {
            hoursPerTick: this.hoursPerTick.peek(),
            totalHours: this.totalHours.peek(),
            fieldResolution: this.fieldResolution.peek(),
            fieldSizeX: this.fieldSizeX.peek(),
            fieldSizeZ: this.fieldSizeZ.peek(),
            fieldSizeD: this.fieldSizeD.peek(),
            terrainList: this.terrainList.map(item => item.save()),
            terrainTimestamp: this.terrainTimestamp.value,
            initNumber: this.initNumber.peek(),
            randomize: this.randomize.peek(),
            renderMode: this.renderMode.peek(),
            initialDate: this.initialDate.peek(),
            latitude: this.latitude.peek(),
            longitude: this.longitude.peek(),
            altitude: this.altitude.peek(),
            azimuthBase: this.azimuthBase.peek(),
            azimuthOffset: this.azimuthOffset.peek(),
            siteTimeZone: this.siteTimeZone.peek(),
            //The weather the run actually ran under, so a reloaded session previews the same sky
            //rather than falling back to a clear one. A plain array: JSON has no typed arrays.
            weather: this.weather.peek() ? Array.from(this.weather.peek()!) : undefined,
            leafAreaIndexObstacles: this.leafAreaIndexObstacles.peek(),
            leafAreaIndexSensors: this.leafAreaIndexSensors.peek(),
            leafAreaIndexDepth: this.leafAreaIndexDepth.peek(),
            leafAreaIndexBandHeight: this.leafAreaIndexBandHeight.peek(),
            lighting: this.lightingOptions(),
            //Its own key rather than a member of `lighting`; see the signal's comment. Older
            //sessions simply lack it and restore to the default.
            lightSky: this.lightSky.peek(),
            viewportBackend: this.viewportBackend.peek(),
            downloadRoots: this.downloadRoots.peek(),
            exactPreview: this.exactPreview.peek(),

            species: this.species.value.map(s => s.save()),
            behaviors: this.behaviors.value,

            seedsPerField: this.seedsPerField.peek(),
            seedsOptimalDistance: this.seedsOptimalDistance.peek(),
            seeds: this.seeds.value.map(s => s.save()),

            obstacles: this.obstacles.value.map(o => o.save()),

            visualMapping: this.visualMapping.peek(),
            debugDisplayOrientations: this.debugBoxes.peek(),
            showLeaves: this.showLeaves.peek(),
            showTerrain: this.showTerrain.peek(),
            showRoots: this.showRoots.peek(),
            showObstacles: this.showObstacles.peek(),
            showSeeds: this.showSeeds.peek(),
            performPicking: this.performPicking.peek(),

            plants: this.plants.peek(),
            //The measurement is a result, like plants: without it a reloaded session cannot show
            //the leaf area index mapping or re-export the numbers.
            obstacleLight: this.obstacleLight.peek(),
            obstacleMapping: this.obstacleMapping.peek(),
            historySize: this.historySize.peek(),
            //History is emitted separately, entry by entry - see below.
            simHoursPerTick: this.simHoursPerTick,

            fieldModelPath: this.fieldModelPath.peek(),
            fieldItemRegex: this.fieldItemRegex.peek(),
            fieldItemRegexMaterial: this.fieldItemRegexMaterial.peek(),
            fieldModelData: this.fieldModelData,
        };

        // Snapshot the index; bytes are streamed one frame at a time, already compressed when
        // the server or a local archive owns them.
        const history = this.history.slice();
        const savedMeasurement = this.archiveMeasurement;
        const fullMeasurement = savedMeasurement
            ? async () => savedMeasurement.stream()
            : runId && this.obstacleLight.peek()?.complete === false ? async () => {
                const response = await fetch(`${location.protocol}//${BackendURI}/simulation/run/${runId}/obstaclelight/packed`);
                if (!response.ok || !response.body) throw new Error('The full measurement could not be saved.');
                return response.body;
            } : undefined;
        return { metadata: data, history, read: async e => {
            const local = localArchiveFrame(e);
            if (local) return local;
            if (e.s) return { bytes: e.s, plain: e.plain };
            if (!runId) throw new Error(`Frame ${e.t} has no stored source.`);
            const response = await fetch(`${location.protocol}//${BackendURI}/simulation/run/${runId}/frame/${e.t}/packed`);
            if (!response.ok) throw new Error(`Could not save frame ${e.t} (HTTP ${response.status}).`);
            return { bytes: new Uint8Array(await response.arrayBuffer()), plain: false };
        }, fullMeasurement, verify: runId ? async () => {
            const response = await fetch(`${location.protocol}//${BackendURI}/simulation/run/${runId}/index`);
            if (!response.ok || (response.headers?.get('X-Agro-Frame-Revision') ?? '0') !== revision)
                throw new Error('The run was resumed or became unavailable during saving. Please retry.');
        } : undefined };
    });

    /**
     * Writes the leaf area index document to a JSON file.
     *
     * The document the server sent, when it is complete. When the socket copy had to drop the
     * per-obstacle series for size, the complete document is fetched from the run store instead -
     * a file the browser saves without parsing, which is why its size does not matter there. A
     * session loaded from an archive saves the copy embedded in it.
     */
    exportObstacleLight = async () => {
        const doc = this.obstacleLight.peek();
        if (!doc)
            return false;
        const runId = this.runId.peek();

        if (this.archiveMeasurement) {
            downloadBlob(this.archiveMeasurement, '-leafareaindex', 'json.gz');
            return true;
        }
        if (!doc.complete && runId) {
            try {
                const res = await fetch(`${location.protocol}//${BackendURI}/simulation/run/${runId}/obstaclelight`);
                if (res.ok) {
                    downloadBlob(await res.blob(), '-leafareaindex', 'json');
                    return true;
                }
                console.warn(`The complete leaf area index document is not on the server (HTTP ${res.status}). `
                    + `Saving the copy that came over the socket, whose totals are complete; see its "omitted" list.`);
            } catch (err) {
                console.warn('Could not fetch the complete leaf area index document; saving the socket copy instead.', err);
            }
        }
        downloadBlob(new Blob([JSON.stringify(doc, undefined, 1)], { type: 'application/json' }), '-leafareaindex', 'json');
        return true;
    };

    load = async() => {
        const input = document.createElement("input");
        input.style.setProperty("display", "none");
        input.type = "file";
        input.accept = ".agro,.json,application/json,application/octet-stream";
        document.body.appendChild(input);
        const self = this;
        input.onchange = async e => {
            const target = e.target as HTMLInputElement;
            const file = target.files?.[0];
            if (!file) { input.remove(); return; }
            try {
                const archive = await readRunArchive(file);
                this.archiveMeasurement = archive?.fullMeasurement;
                const data = archive?.metadata ?? JSON.parse(await file.text());
                const seeds = self.seeds.peek();
                for(let i = seeds.length - 1; i >= 0; --i)
                    self.removeSeedAt(i);

                const obstacles = self.obstacles.peek();
                for(let i = obstacles.length - 1; i >= 0; --i)
                    self.removeObstacleAt(i);

                self.history = archive?.history ?? decodeSavedHistory(data.history);
                clearSceneCache();
                resetCompression();
                //A loaded session arrives entirely raw and would otherwise stay that way: the
                //compressor is normally driven by frames arriving from a run, and loading is the
                //other way a large history appears.
                scheduleCompression();
                self.simHoursPerTick = data.simHoursPerTick ?? 1;

                batch(() => {
                    self.hoursPerTick.value = data.hoursPerTick;
                    self.totalHours.value = data.totalHours;
                    self.fieldResolution.value = data.fieldResolution;
                    self.fieldSizeX.value = data.fieldSizeX;
                    self.fieldSizeZ.value = data.fieldSizeZ;
                    self.fieldSizeD.value = data.fieldSizeD;
                    self.terrainList = data.terrainList?.map((item, index) => item.hasOwnProperty('points') ? MeshTerrainItem.load(item, index) : BoxTerrainItem.load(item, index)) ?? [];
                    //Bumped, not restored. The saved number is a revision marker from another
                    //session, and a session saved and reloaded in the same tab can carry the value
                    //the signal already holds - which is no change, so the viewport keeps drawing
                    //the terrain the file was supposed to replace.
                    self.bumpTerrain();
                    self.initNumber.value = data.initNumber;
                    self.randomize.value = data.randomize;
                    self.renderMode.value = data.renderMode;
                    self.initialDate.value = data.initialDate ?? DEFAULT_INITIAL_DATE;
                    //Sessions saved before the site was a setting were all at the default one.
                    self.latitude.value = data.latitude ?? LATITUDE;
                    self.longitude.value = data.longitude ?? LONGITUDE;
                    self.altitude.value = data.altitude ?? ALTITUDE;
                    self.azimuthBase.value = data.azimuthBase ?? AZIMUTH;
                    self.azimuthOffset.value = data.azimuthOffset ?? 0;
                    self.siteTimeZone.value = data.siteTimeZone ?? undefined;
                    self.weather.value = Array.isArray(data.weather)
                        ? Float32Array.from(data.weather) : undefined;
                    //Sessions saved before the measurement existed carry none of these.
                    self.leafAreaIndexObstacles.value = data.leafAreaIndexObstacles ?? "";
                    self.leafAreaIndexSensors.value = data.leafAreaIndexSensors ?? 32768;
                    self.leafAreaIndexDepth.value = data.leafAreaIndexDepth ?? 3;
                    self.leafAreaIndexBandHeight.value = data.leafAreaIndexBandHeight ?? 0.5;
                    //Sessions saved before the document existed carry the measurement in an
                    //older shape; it is dropped rather than half-read into the new one.
                    self.obstacleLight.value = isObstacleLightDocument(data.obstacleLight) ? data.obstacleLight : undefined;
                    if (data.obstacleLight && !self.obstacleLight.value)
                        console.warn('The session carries a leaf area index measurement in an older format, which this build cannot show. Run the simulation again to measure it.');
                    self.obstacleMapping.value = data.obstacleMapping ?? ObstacleMappingOptions.None;
                    //Sessions saved before the ladder became independent switches carry a
                    //single number instead; translate rather than dropping their setting.
                    const lighting: LightingOptions = data.lighting
                        ?? (data.lightingTier !== undefined ? lightingFromLegacyTier(data.lightingTier) : LIGHTING_OFF);
                    self.lightDaylight.value = lighting.daylight ?? false;
                    self.lightShadows.value = lighting.shadows;
                    self.lightOcclusion.value = lighting.occlusion;
                    self.lightIndirect.value = lighting.indirect;
                    self.lightSecondBounce.value = lighting.secondBounce;
                    self.lightLeafTranslucency.value = lighting.leafTranslucency ?? false;
                    self.shadowBiasTexels.value = lighting.shadowBiasTexels ?? SHADOW_NORMAL_OFFSET_TEXELS;
                    //Sessions saved before the sky existed - including every legacy tier one -
                    //land on the default, which is off.
                    self.lightSky.value = data.lightSky ?? false;
                    self.viewportBackend.value = data.viewportBackend ?? "auto";
                    self.downloadRoots.value = data.downloadRoots;
                    self.exactPreview.value = data.exactPreview;

                    //Through the same adopter as a stored run, for the same reason: `load()` copies
                    //settings and no geometry, so a replaced species list draws every leaf and
                    //petal as the flat fallback quad until something re-fetches the outlines.
                    self.species.value = adoptSpeciesGeometry(
                        data.species.map(s => new Species().load(s)), self.species.peek());
                    self.behaviors.value = data.behaviors;

                    self.seedsPerField.value = data.seedsPerField;
                    self.seedsOptimalDistance.value = data.seedsOptimalDistance;
                    self.seeds.value = data.seeds.map(s => new Seed(s.species, s.px, s. py, s.pz, s.fi, false));

                    self.obstacles.value = data.obstacles.map(o => {
                        const obstacle = new Obstacle(o.type, o.px, o.py, o.pz, o.ax, o.ay, o.l, o.h, o.t, new Float32Array(o.vt), o.fc);
                        //Sessions saved before the group table existed simply have no mapping.
                        obstacle.groups = o.gr;
                        return obstacle;
                    });

                    self.visualMapping.value = data.visualMapping;
                    self.debugBoxes.value = data.debugDisplayOrientations;
                    self.showLeaves.value = data.showLeaves;
                    self.showTerrain.value = data.showTerrain;
                    self.showRoots.value = data.showRoots;
                    self.showObstacles.value = data.showObstacles;
                    self.showSeeds.value = data.showSeeds;

                    self.performPicking.value = data.performPicking;

                    self.plants.value = data.plants;
                    //Measured, not read back - see measureHistoryBytes for why the recorded
                    //figure describes a different history than the one just loaded.
                    self.historySize.value = measureHistoryBytes(self.history);
                    self.historyCount.value = self.history.length;
                    //A loaded session has received nothing on the wire; every frame is simply held.
                    lastReceivedIndex = self.history.length - 1;
                    //A loaded session is not the run this tab may have just computed. Leaving the
                    //id in place points eviction and every re-fetch at a run that does not contain
                    //these frames: the payloads would be dropped as though the server held them,
                    //and fetching one back returns the SAME step of a DIFFERENT simulation, which
                    //decodes perfectly and is simply the wrong plants.
                    self.runId.value = '';
                    self.historyBudget.value = historyBudgetBytes(false);
                    self.evictedFrames.value = 0;
                    self.historyLimitHit.value = false;
                    try { sessionStorage.removeItem('agro.runId'); } catch { /* private mode */ }
                    //History was already rehydrated above, before this batch. It used to be
                    //assigned twice - harmless while it was a plain reference copy, but
                    //decodeSavedHistory consumes its source as it converts, so a second call
                    //saw entries it had already released and threw on `e.t`.
                    self.simHoursPerTick = data.simHoursPerTick;

                    self.fieldModelPath.value = data.fieldModelPath;
                    self.fieldItemRegex.value = data.fieldItemRegex;
                    self.fieldItemRegexMaterial.value = data.fieldItemRegexMaterial;
                    self.fieldModelData = data.fieldModelData;

                    showSceneAt(0);
                });

                self.play(PlayState.SeekBackward);

                input.remove();
            } catch (error) {
                console.error('Could not load simulation:', error);
                alert(error instanceof Error ? error.message : String(error));
            } finally { input.remove(); }
        };
        input.click();
    }

    gltf = () => {
        //The scene graph lives in the renderer now, so it does the export. Backends that
        //cannot export simply do not implement the optional method.
        const renderer = activeRenderer.current;
        if (renderer?.exportGLTF)
            renderer.exportGLTF(json => this.saveTextFile(json, 'gltf'));
        else
            console.warn('The active viewport does not support glTF export.');
    }

    prim = async () => {
        const i = Math.min(this.playPointer.peek(), st.history.length - 1);
        if (i < 0) return;
        const e = this.history[i];
        this.saveBinaryFile(await rawFrameBytes(e), `_${e.t}`, 'prim');
    }

    uploadFieldModel = async (f: File) => {
        //An explicit upload is the more recent intent, so it replaces whatever stored run was
        //going to supply the terrain. Leaving it set would send the server looking for a model
        //that the user has just decided against.
        this.fieldModelRunId.value = '';
        this.fieldModelData = await Parse(f);
        // const bytes = new Uint8Array(await f.arrayBuffer());
        // this.fieldModelData = bytes;
        this.fieldModelPath.value = f.name;
        if (hubConnection.state !== SignalR.HubConnectionState.Connected)
            await start();

        if (hubConnection.state == SignalR.HubConnectionState.Connected)
        {
            const bufferResponse = await fetch(`${location.protocol}//${BackendURI}/simulation/terrain`, {
                body: this.fieldModelData,
                method: 'post',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'text/plain'
                }
            });
            const terrainId = await bufferResponse.text();
            hubConnection.invoke("terrain", terrainId, this.hoursPerTick.value, this.fieldItemRegex.value, this.fieldItemRegexMaterial.value, this.fieldResolution.value);
        }
    }

    clearFieldModel = () => {
        this.fieldModelData = undefined;
        this.fieldModelPath.value = "";
        this.fieldModelRunId.value = '';
    }


    uploadSeedsDistribution = async (f: File) => {
        const text = await f.text();
        const data = JSON.parse(text) as ITrayModel[];

        this.clearSeeds();

        const speciesMap = new Map<string, Species>();
        const normalize = (s: string) => {
            return s?.toLowerCase()
                    .normalize('NFD') // Decomposes accented characters
                    .replace(/[\u0300-\u036f]/g, '') // Removes diacritics
                    .replace(/[^a-zA-Z0-9\s]/g, '') // Removes special characters
                    .replace(/\s{1,}/g, ' ') //Remove multiple spaecs
                    .trim();
        };
        this.species.value.forEach(x => {
            if (x.name.value?.length > 0)
                speciesMap.set(normalize(x.name.value), x);
            if (x.aka.value?.length > 0)
                speciesMap.set(normalize(x.aka.value), x);
        });

        const speciesWordsMap : {words: string[], key: string}[] = [];
        Array.from(speciesMap.keys()).forEach(key => {
            speciesWordsMap.push({words: key.split(" "), key});
        });

        function bestSpeciesWordMatch(input: string)
        {
            const needle = input.split(" ");
            let bestValue = -1;
            let bestKey;
            speciesWordsMap.forEach(item => {
                let i = 0;
                for(; i < needle.length && i < item.words.length && needle[i] === item.words[i]; ++i) { }
                if (i > bestValue)
                {
                    bestValue = i;
                    bestKey = item.key;
                }
            });

            if (bestKey)
            {
                const result = speciesMap.get(bestKey);
                if (result) speciesMap.set(input, result);
                return result;
            }
            return undefined;
        }

        const result : Seed[] = [];
        data.forEach(section => {
            section.plants.forEach(seed => {
                const options : string[] = [];
                if (seed.species)
                    options.push(normalize(seed.species));
                if (seed.speciesName)
                    options.push(normalize(seed.speciesName));
                if (seed.botanicalName)
                    options.push(normalize(seed.botanicalName));

                let species : Species | undefined;

                for(let i = 0; i < options.length; ++i)
                    if (!species) species = speciesMap.get(options[i]);

                if (!species)
                {
                    for(let i = 0; i < options.length; ++i)
                        if (!species)
                            species = bestSpeciesWordMatch(options[i]);
                }

                if (species)
                {
                    const sectionIndex = this.terrainList.findIndex(x => x.id == section.sectionId);
                    if (sectionIndex >= 0)
                    {
                        seed = convertCoordsTo_RH_rightPX_upPY(seed, CoordinateSystem.RH_rightPX_upPZ);
                        result.push(new Seed(species.name.peek(), seed.x, seed.y, seed.z, sectionIndex, true));
                    }
                }
            });
        });
        this.seeds.value = [ ...result]

        // this.seedsDistributionData = await f.text();
        // this.seedsDistributionPath.value = f.name;
        // if (hubConnection.state !== SignalR.HubConnectionState.Connected)
        //     await start();

        // if (hubConnection.state == SignalR.HubConnectionState.Connected)
        // {
        //     const bufferResponse = await fetch(`${location.protocol}//${BackendURI}/simulation/seeds`, {
        //         body: this.seedsDistributionData,
        //         method: 'post',
        //         headers: {
        //             'Accept': 'application/json',
        //             'Content-Type': 'application/json'
        //         }
        //     });
        //     const distributionId = await bufferResponse.text();
        //     hubConnection.invoke("seeds", distributionId);
        // }
    }

    clearSeedsDistribution = () => {
        this.seedsDistributionData = undefined;
        this.seedsDistributionPath.value = "";
    }

    speciesLinksCount(species: string) {
        return this.seeds.value.reduce((a, c) => a + (c.species.value == species ? 1 : 0), 0)
    }

    //--- the archive of stored runs -------------------------------------------------------
    //
    //A run outlives the tab that started it: the server keeps its frames, its request, its soil
    //and its measurement. What was missing was any way back to one. These five methods are that
    //way - list them, open one, delete one, and start a new run from one either unchanged
    //(continue) or as a starting point to edit (fork).
    //
    //Neither continue nor fork RESUMES anything, and the wording in the panel says so. Nothing in
    //this project serializes live world state, so a child run is computed again from its parent's
    //recorded request. For a deterministic parent that reproduces it exactly and the comparison
    //means something; for any other it is a second sample of the same scenario.

    refreshRuns = async () => {
        this.runsLoading.value = true;
        try {
            const res = await fetch(`${location.protocol}//${BackendURI}/simulation/runs?limit=${RUNS_PAGE_LIMIT}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const listing = await res.json() as IStoredRun[];
            batch(() => {
                this.runs.value = listing;
                this.runsError.value = '';
            });
        } catch (err) {
            this.runsError.value = `Could not list the stored runs: ${err}`;
            console.warn('Could not list the stored runs:', err);
        } finally {
            this.runsLoading.value = false;
        }
    }

    /**
     * Opens a stored run for viewing: its soil, its scenario and its whole timeline.
     *
     * The fetches are not interchangeable in order. Seeds are positioned against `terrainList` -
     * `new Seed` reads the tray it belongs to - so the terrain has to be in place before the
     * request is applied, and the timeline comes last because showing a frame needs both.
     *
     * Everything this tab was holding goes first, deliberately. A stored run is a different
     * simulation from whatever was on screen, and keeping the old history would leave frames in
     * the timeline that the new run id cannot serve: re-fetching one returns the same step of a
     * DIFFERENT simulation, which decodes perfectly and is simply the wrong plants.
     */
    loadStoredRun = (runId: string): Promise<boolean> => this.openStoredRun(runId, false);

    /**
     * The shared body of View and Fork.
     *
     * They differ in one thing: View is about the frames and is pointless without them, while a
     * Fork wants the SCENARIO - which survives even when the frames do not. A run stopped before
     * its first step has a complete request.json and an empty frames directory, and its settings
     * are as forkable as any other run's.
     */
    private openStoredRun = async (runId: string, allowEmpty: boolean): Promise<boolean> => {
        if (this.computing.peek()) {
            this.runsError.value = 'A simulation is running in this tab. Stop it before opening a stored run.';
            return false;
        }
        //One at a time, globally and not per row. Two opens in flight interleave their terrain,
        //scenario and timeline writes into one tab, and the result is a scene assembled from two
        //different simulations with nothing to say which parts came from where.
        if (this.runsBusy.peek()) {
            this.runsError.value = 'Still opening the previous run.';
            return false;
        }
        this.runsBusy.value = runId;
        try {
            const meta = await fetchStoredRun(runId);
            if (!meta) {
                this.runsError.value = `Run ${runId} is no longer on the server.`;
                await this.refreshRuns();
                return false;
            }
            //Checked BEFORE anything is torn down. A run can genuinely hold no frames - one that
            //was stopped before its first step, which every store accumulates - and finding that
            //out after clearing the timeline costs the user the scene they were looking at in
            //exchange for an error message.
            const hasFrames = meta.storedFrames > 0;
            if (!hasFrames && !allowEmpty) {
                this.runsError.value = `Run ${runId} stored no frames; there is nothing to show.`;
                return false;
            }

            batch(() => {
                this.playing.value = PlayState.None;
                this.playPointer.value = 0;
                //The scene, not only the timeline. `syncTimelineToIndex` redraws only when the
                //pointer MOVES, so a run with a single stored frame lands on pointer 0 - where the
                //pointer already was - and leaves the previous run's plants on screen under this
                //run's name.
                this.scene.value = [];
                this.historySize.value = 0;
                this.historyCount.value = 0;
                this.historyLimitHit.value = false;
                this.evictedFrames.value = 0;
                this.renderer.value = '';
                this.simLength.value = 0;
                this.simStep.value = 0;
                //A stored run carries no measurement on the wire. Leaving the previous one would
                //colour these obstacles by numbers belonging to a scene that is no longer shown.
                this.obstacleLight.value = undefined;
                this.plants.value = [];
            });
            this.history.length = 0;
            this.archiveMeasurement = undefined;
            clearSceneCache();
            resetCompression();

            //Whatever fork was being set up, this run supersedes it. Carrying the old values over
            //would file the next simulation as a child of a run nobody is looking at any more, and
            //build it on a facade belonging to a third one. Fork and Continue set them again
            //immediately afterwards, for the run the user actually picked.
            batch(() => {
                this.lineageParentRunId.value = '';
                this.fieldModelRunId.value = '';
            });

            //The soil as the socket delivered it when the model was uploaded. A run stored before
            //terrains were kept simply has none, and draws its plants over the default field -
            //which is why the panel says whether a run has one before offering to open it.
            await this.restoreStoredTerrain(runId, meta.hasTerrain, meta.fieldModelName);
            await this.restoreStoredScenario(runId);

            this.runsError.value = '';
            //A run with no frames leaves the tab pointed at nothing. Deliberate: `runId` is what
            //makes dropping a payload from memory safe, and an id that serves no frames turns that
            //promise into a lie the moment a new run fills the history.
            if (!hasFrames) {
                console.log(`Loaded the scenario of run ${runId}; it stored no frames.`);
                return true;
            }

            batch(() => {
                this.runId.value = runId;
                this.viewingRunId.value = runId;
                //The clock multiplies a frame's step by this; without it the run is drawn on the
                //wrong timeline and nothing else on the page could tell.
                this.simHoursPerTick = meta.hoursPerTick;
            });
            refreshHistoryBudget();
            //Reattaching this tab to a run it did not start is what makes the id survive a reload.
            try { sessionStorage.setItem('agro.runId', runId); } catch { /* private mode */ }

            const frames = await syncTimelineToIndex(runId, true);
            if (frames === 0) {
                this.runsError.value = `Run ${runId} has no stored frames to show.`;
                return false;
            }
            console.log(`Opened stored run ${runId}: ${frames} frames, ${meta.plants} plants.`);
            return true;
        } catch (err) {
            this.runsError.value = `Could not open run ${runId}: ${err}`;
            console.warn(`Could not open run ${runId}:`, err);
            return false;
        } finally {
            this.runsBusy.value = '';
        }
    }

    /**
     * Rebuilds the trays and the imported obstacles of a stored run.
     *
     * The same bytes and the same decoder as the socket's `terrain` message - this is that message
     * fetched over REST instead of pushed. Mesh obstacles are replaced rather than appended, for
     * the same reason the socket handler replaces them: they belong to the model, not to the user.
     */
    restoreStoredTerrain = async (runId: string, available: boolean, modelName?: string | null) => {
        if (available) {
            try {
                const res = await fetch(`${location.protocol}//${BackendURI}/simulation/run/${runId}/terrain`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const reader = new BinaryReader(new Uint8Array(await res.arrayBuffer()));
                const result = reader.readTerrain();
                this.terrainList = result.terrains;
                this.obstacles.value = [...this.obstacles.peek().filter(x => x.type.value !== 'mesh'), ...result.obstacles];
                this.bumpTerrain();
                return;
            } catch (err) {
                console.warn(`Could not read the terrain of run ${runId}:`, err);
            }
        }

        //Nothing stored, or it could not be read. Keep what this tab is already showing when it
        //came from the SAME model file the run names - a user who has just uploaded that facade,
        //or who opened a sibling run built on it, is looking at the right trays already, and
        //throwing them away to show nothing is the worse of the two wrong answers. `fieldModelPath`
        //still holds the previous scenario's model here; the new one is applied afterwards.
        const loaded = this.fieldModelPath.peek();
        if (modelName && loaded && modelName === loaded && this.terrainList.length > 0) {
            console.log(`Run ${runId} has no stored terrain; keeping the loaded '${loaded}', which is the model it names.`);
            return;
        }

        //Otherwise clear, rather than draw this run's plants inside somebody else's trays.
        this.terrainList = [];
        this.obstacles.value = this.obstacles.peek().filter(x => x.type.value !== 'mesh');
        this.bumpTerrain();
    }

    /**
     * Marks the terrain dirty for the viewport.
     *
     * `terrainList` is a plain array, so nothing reacts to it: `terrainTimestamp` is the only thing
     * the renderer subscribes to. A counter rather than `Date.now()`, which it used to be - two
     * terrain loads inside one millisecond wrote the same value, the signal saw no change, and the
     * trays sat in appstate while the screen kept the previous scene.
     */
    bumpTerrain() {
        this.terrainTimestamp.value = this.terrainTimestamp.peek() + 1;
    }

    /**
     * Puts a stored run's scenario back into the editor.
     *
     * The stored request is the SERVER's shape, not the session file's: PascalCase, angles in
     * radians, and short keys inside `Plants` and `Obstacles` (`S`/`P`/`F`, `T`/`H`/`D`/`O`/`L`).
     * That is what makes this its own function rather than a call to `load()` - the two formats
     * describe the same scenario and share not one key.
     */
    restoreStoredScenario = async (runId: string) => {
        //Ordered against the startup fetch rather than racing it - see `speciesReady`.
        await speciesReady;
        const res = await fetch(`${location.protocol}//${BackendURI}/simulation/run/${runId}/request`);
        if (!res.ok) throw new Error(`the stored request is missing (HTTP ${res.status})`);
        const response = await res.json();

        //Replaced wholesale rather than removed one at a time. `removeSeedAt` copies the whole
        //array and writes the signal for EACH removal, so clearing a 4,134-plant stand that way is
        //four thousand array copies and four thousand re-renders before the first new seed exists -
        //on precisely the runs this panel is for. The assignments below replace both lists anyway.
        const meshObstacles = this.obstacles.peek().filter(x => x.type.peek() === 'mesh');

        batch(() => {
            this.hoursPerTick.value = response.HoursPerTick ?? 1;
            this.totalHours.value = response.TotalHours ?? 744;
            this.fieldResolution.value = response.FieldResolution ?? 0.5;
            if (response.FieldSize) {
                this.fieldSizeX.value = response.FieldSize.X;
                this.fieldSizeD.value = response.FieldSize.D;
                this.fieldSizeZ.value = response.FieldSize.Z;
            }
            //`randomize` off, because the stored seed is the point: a run reopened to be forked
            //should start from the number that produced it, not from a fresh random one.
            this.initNumber.value = Number(response.Seed ?? 42);
            this.randomize.value = false;
            //A CALENDAR DAY, not an instant. The request stores a C# DateTime, so this arrives as
            //"2026-02-01T00:00:00", while everything downstream treats initialDate as YYYY-MM-DD:
            //`<input type="date">` blanks anything else, `localMidnightToUtc` does
            //`split('-').map(Number)` and gets NaN for the day, and `new Date(`${date}T11:00:00Z`)`
            //becomes an Invalid Date - which propagates as a NaN sun altitude that never trips the
            //`altitude <= 0` night check, so the shadow map is pointed in a direction made of NaN.
            this.initialDate.value = response.InitialDate
                ? String(response.InitialDate).slice(0, 10) : DEFAULT_INITIAL_DATE;
            //Defaults matched to Agro/AgroWorld.cs, so importing a request written before the
            //site was settable reproduces the run it describes rather than the current form.
            this.latitude.value = response.Latitude ?? LATITUDE;
            this.longitude.value = response.Longitude ?? LONGITUDE;
            this.altitude.value = response.Altitude ?? ALTITUDE;
            this.azimuthBase.value = response.AzimuthBase ?? AZIMUTH;
            this.azimuthOffset.value = response.AzimuthOffset ?? 0;
            this.renderMode.value = response.RenderMode ?? 0;
            if (response.SamplesPerPixel) this.samplesPerPixel.value = response.SamplesPerPixel;
            this.deterministic.value = response.Deterministic ?? false;
            this.contactEngine.value = response.ContactEngine ?? 0;
            this.exactPreview.value = response.ExactPreview ?? false;
            this.downloadRoots.value = response.DownloadRoots ?? false;

            this.leafAreaIndexObstacles.value = response.LeafAreaIndexObstacles ?? '';
            this.leafAreaIndexSensors.value = response.LeafAreaIndexSensors ?? 32768;
            this.leafAreaIndexDepth.value = response.LeafAreaIndexDepth ?? 3;
            this.leafAreaIndexBandHeight.value = response.LeafAreaIndexBandHeight ?? 0.5;

            this.fieldModelPath.value = response.FieldModelPath ?? '';
            this.fieldItemRegex.value = response.FieldItemRegex ?? 'erde';
            this.fieldItemRegexMaterial.value = response.FieldItemRegexMaterial ?? false;
            //The OBJ itself is on the SERVER, not here. Nothing in this tab can re-upload it, and
            //nothing needs to: a run started from here names the stored run instead.
            this.fieldModelData = undefined;

            if (Array.isArray(response.Species) && response.Species.length > 0)
                this.species.value = adoptSpeciesGeometry(
                    response.Species.map((x: any) => new Species().load(camelizeTopLevel(x))),
                    this.species.peek());

            //Unconditional, so a run with no seeds clears the previous scenario's rather than
            //inheriting them - the panel would then show plants this run never had.
            this.seeds.value = Array.isArray(response.Plants)
                ? response.Plants.map((p: any) =>
                    new Seed(p.S ?? '', p.P?.X ?? 0, p.P?.Y ?? 0, p.P?.Z ?? 0, p.F ?? 0, false))
                : [];

            //Only the primitives are rebuilt here. An imported model's obstacles travel as ONE
            //'mesh' placeholder in the request and as real geometry in the terrain, which has
            //already been applied - rebuilding the placeholder would add an empty mesh on top of
            //the real one.
            this.obstacles.value = [
                ...meshObstacles,
                ...(Array.isArray(response.Obstacles) ? response.Obstacles : [])
                    .filter((o: any) => o.T === 'wall' || o.T === 'umbrella')
                    .map((o: any) => new Obstacle(o.T, o.P?.X ?? 0, o.P?.Y ?? 0, o.P?.Z ?? 0,
                        0, o.O ?? 0, (o.T === 'umbrella' ? o.R : o.L) ?? 0, o.H ?? 0, o.D ?? 0,
                        new Float32Array(0), [])),
            ];
        });

        //The zone the server derived from these coordinates, and the weather the run ran under.
        //Neither is in the request: the zone comes from a coordinate lookup the browser has no
        //equivalent for, and the weather is planned from the seed. Without them a reopened run at
        //a moved site keeps the DEFAULT zone - shifting every sun position and the whole sky by
        //the offset, while still looking entirely plausible - and previews a clear sky over steps
        //the simulation ran under cloud.
        await this.restoreStoredSite(runId);
        this.needsRender.value = true;
    }

    /**
     * The site and the weather of a stored run, recomputed server-side from its own request.
     *
     * Failure is warned about and swallowed rather than rethrown: the scenario is already
     * restored and the run is perfectly openable. What is lost is the sky's fidelity, and both
     * fallbacks - the default zone and a clear sky - are documented behaviour, not an error.
     */
    private restoreStoredSite = async (runId: string) => {
        try {
            const res = await fetch(`${location.protocol}//${BackendURI}/simulation/run/${runId}/site`);
            if (!res.ok) return;
            const site = await res.json();
            batch(() => {
                this.latitude.value = site.latitude ?? this.latitude.peek();
                this.longitude.value = site.longitude ?? this.longitude.peek();
                this.altitude.value = site.altitude ?? this.altitude.peek();
                this.azimuthBase.value = site.azimuthBase ?? this.azimuthBase.peek();
                this.azimuthOffset.value = site.azimuthOffset ?? this.azimuthOffset.peek();
                this.siteTimeZone.value = site.timeZone || undefined;
                this.weather.value = decodeSkyCoverage(site.skyCoverage);
            });
        } catch (err) {
            console.warn(`Could not restore the site of run ${runId}:`, err);
        }
    };

    /**
     * Deletes a stored run, after asking.
     *
     * The only way a user gets bytes back on their own terms - eviction is oldest-first and knows
     * nothing about which run mattered. Irreversible and often tens of gigabytes, hence the
     * confirmation, and hence quoting the size in it.
     */
    deleteStoredRun = async (run: IStoredRun): Promise<boolean> => {
        if (this.runsBusy.peek()) {
            this.runsError.value = 'Another run is being opened; try again in a moment.';
            return false;
        }
        this.runsBusy.value = run.id;
        try {
            const res = await fetch(`${location.protocol}//${BackendURI}/simulation/run/${run.id}`, { method: 'DELETE' });
            const body = await res.json().catch(() => ({ deleted: res.ok, reason: `HTTP ${res.status}` }));
            if (!body.deleted) {
                this.runsError.value = body.reason || `Could not delete run ${run.id}.`;
                return false;
            }
            //This tab was showing the run that has just gone. Clearing the id is not enough: the
            //timeline it left behind is mostly entries whose payloads live on the server, so
            //scrubbing onto one would fetch a run that no longer exists and quietly draw nothing.
            //Better an empty viewport than a timeline that lies about what it can show.
            if (this.runId.peek() === run.id) {
                this.history.length = 0;
            this.archiveMeasurement = undefined;
                clearSceneCache();
                resetCompression();
                batch(() => {
                    this.viewingRunId.value = '';
                    this.historyCount.value = 0;
                    this.historySize.value = 0;
                    this.evictedFrames.value = 0;
                    this.playPointer.value = 0;
                    this.playing.value = PlayState.None;
                    this.scene.value = [];
                    this.plants.value = [];
                });
                //Drops the id, restores the no-server memory budget and forgets the reload hint.
                forgetRun();
            }
            if (this.lineageParentRunId.peek() === run.id) this.lineageParentRunId.value = '';
            if (this.fieldModelRunId.peek() === run.id) this.fieldModelRunId.value = '';
            this.runsError.value = '';
            await this.refreshRuns();
            return true;
        } catch (err) {
            this.runsError.value = `Could not delete run ${run.id}: ${err}`;
            return false;
        } finally {
            this.runsBusy.value = '';
        }
    }

    /**
     * Loads a stored run's scenario into the editor as the starting point for a new one.
     *
     * Stops there on purpose: a fork exists to be CHANGED, so it opens the run, records where the
     * new run will come from, and leaves the user on the simulation tab with everything editable.
     * Pressing Simulate is the fork.
     */
    forkStoredRun = async (run: IStoredRun): Promise<boolean> => {
        //`allowEmpty`, because a fork wants the settings and a run that stored no frames still has
        //all of them.
        if (!await this.openStoredRun(run.id, true)) return false;
        batch(() => {
            this.lineageParentRunId.value = run.id;
            //Only when the model is actually still there. Pointing at a run whose model has been
            //collected would have the server silently fall back to a flat default field.
            this.fieldModelRunId.value = run.hasFieldModel ? run.id : '';
            this.activeTab.value = 'tab-sim';
        });
        return true;
    }

    /**
     * Carries an incomplete run on from where it stopped, without recomputing a step of it.
     *
     * The real thing, as opposed to `continueStoredRun`: the server restores the world from the
     * checkpoint it wrote and the loop picks up at that step, so a run stopped at simulated month
     * four resumes at month four rather than spending the compute again to get back there. The same
     * path extends a FINISHED run past the horizon it was given, because a completed run writes a
     * checkpoint too.
     *
     * Everything but the total comes from the run itself. A world grown under one set of parameters
     * cannot be handed different ones half way through and still be one experiment.
     */
    resumeStoredRun = async (run: IStoredRun, extraHours: number): Promise<boolean> => {
        if (this.computing.peek()) {
            this.runsError.value = 'A simulation is running in this tab. Stop it before resuming another.';
            return false;
        }
        if (!run.hasCheckpoint) {
            this.runsError.value = `Run ${run.id} has no checkpoint, so it cannot be resumed. Fork it to recompute it instead.`;
            return false;
        }
        //Opened first, so the tab is showing the run it is about to extend - and so its timeline,
        //trays and scenario are the ones the new frames will append to.
        if (!await this.openStoredRun(run.id, true)) return false;

        const total = Math.max(1, (run.totalHours || this.totalHours.peek()) + Math.max(0, extraHours));
        batch(() => {
            this.totalHours.value = total;
            this.computing.value = true;
            this.playing.value = PlayState.ForwardWaiting;
            this.simLength.value = 0;
            this.lineageParentRunId.value = '';
            this.fieldModelRunId.value = '';
        });

        if (hubConnection.state !== SignalR.HubConnectionState.Connected) await start();
        if (hubConnection.state !== SignalR.HubConnectionState.Connected) {
            this.computing.value = false;
            this.runsError.value = 'Not connected to the server.';
            return false;
        }

        //Through the same upload-then-start path a normal run takes; only ResumeRunId differs, and
        //the server reads the scenario from the run rather than from this body.
        const body = { ...this.requestBodyForResume(), TotalHours: Math.trunc(total), ResumeRunId: run.id };
        try {
            const prepared = await fetch(`${location.protocol}//${BackendURI}/simulation/upload`, {
                body: JSON.stringify(body), method: 'post',
                headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            });
            const preparedID = await prepared.text();
            if (!(preparedID?.length > 0)) throw new Error('the server did not accept the request');
            await hubConnection.invoke('start', preparedID);
            this.previewRequest.value = true;
            this.runsError.value = '';
            return true;
        } catch (err) {
            batch(() => { this.computing.value = false; this.renderer.value = 'error'; });
            //The server refuses a resume it cannot honour - no checkpoint, a checkpoint that does
            //not fit, or a total the run has already passed - and says which.
            this.runsError.value = `Could not resume run ${run.id}: ${(err as any)?.message ?? err}`;
            return false;
        }
    }

    /** The request body a resume sends. Only TotalHours and ResumeRunId are read by the server. */
    private requestBodyForResume = () => ({
        HoursPerTick: Math.trunc(this.hoursPerTick.peek()),
        TotalHours: Math.trunc(this.totalHours.peek()),
        RequestGeometry: true,
    });

    /**
     * Starts a new run that carries a stored one further in simulated time.
     *
     * It is a RE-RUN, not a resume, and the panel says so: the world is rebuilt from the recorded
     * request and computed from step zero to the new total. There is no checkpoint of live world
     * state to start from - the store holds exported geometry, which is a picture of the plants
     * and not the plants. For a deterministic parent the first stretch reproduces it exactly, so
     * the extension is genuinely a continuation; otherwise it is a fresh sample of the scenario.
     */
    continueStoredRun = async (run: IStoredRun, extraHours: number): Promise<boolean> => {
        if (!await this.openStoredRun(run.id, true)) return false;
        batch(() => {
            this.totalHours.value = Math.max(1, (run.totalHours || this.totalHours.peek()) + Math.max(0, extraHours));
            this.lineageParentRunId.value = run.id;
            this.fieldModelRunId.value = run.hasFieldModel ? run.id : '';
            this.activeTab.value = 'tab-home';
        });
        await this.run();
        return true;
    }

    private saveTextFile(data: string, ext: string) {
        downloadText(data, ext);
    }

    private saveBinaryFile(data: Uint8Array, frame: string, ext: string) {
        //The cast is the generic-Uint8Array/BlobPart variance in newer lib.dom typings, not a
        //real mismatch - Blob accepts any ArrayBufferView. Copying to satisfy it would duplicate
        //a 60 MB frame for nothing.
        downloadBinary(data as unknown as BlobPart, ext, frame);
    }
}

const st = new State();
/**
 * The cloud cover to preview a timestep under.
 *
 * A free function taking the series rather than a method reading the signal, so a caller in a
 * reactive effect can read `weather.value` itself and pass it in - a bare read for its
 * subscription alone is deleted by terser, which is how a signal subscription silently dies in
 * production only.
 *
 * No series means a CLEAR sky. The preview then shows the clear-sky dome for the instant, which
 * differs from the simulation's on an overcast step; that is an honest limitation of an older
 * run, not a failure.
 */
export function skyCoverageAt(series: Float32Array | undefined, timestep: number): number {
    if (!series || series.length === 0) return 0;
    const i = timestep < 0 ? 0 : timestep >= series.length ? series.length - 1 : timestep;
    const v = series[i];
    return v > 0 ? (v < 1 ? v : 1) : 0;
}

export default st;
//A run this tab started may still be computing, or may have finished while the page was away.
void recoverRun();
//now that the singleton is exported push in the default seed
st.seeds.value = [ new Seed(st.species.peek()[0].name.peek(), st.fieldSizeX.peek() * 0.5, -0.01, st.fieldSizeZ.peek() * 0.5, 0, false) ];
speciesReady = fetch(`${location.protocol}//${BackendURI}/Simulation/species`, { headers: { Accept: 'application/json', 'Content-Type': 'application/json' }}).then(response => response.json()).then((list: Species[]) => {
    const species = list.map(x => new Species().load(x));
    //One place fetches leaf and petal outlines, so that replacing the species list - which opening
    //a stored run and loading a session both do - cannot quietly skip it.
    void fetchSpeciesGeometry(species);
    st.species.value = species;
});

fetch(`${location.protocol}//${BackendURI}/Simulation/behaviors`, { headers: { Accept: 'application/json', 'Content-Type': 'application/json' }}).then(response => response.json()).then((list: string[]) => st.behaviors.value = list);

effect(() => {
    //Depending on hubConnected is what lets a request survive being made too early: the
    //effect re-runs when the socket comes up and flushes it. The short-circuit is safe here
    //only because the second read happens whenever the first is true, which is the only case
    //that does anything.
    if (st.previewRequest.value && hubConnected.value) {
        //The hub can drop between the state check and the call, and an unhandled rejection
        //would take out the effect. Re-arm only if the connection actually went away: that
        //case is picked up again when hubConnected flips back, whereas re-arming on a
        //server-side failure while still connected would spin the effect.
        hubConnection.invoke("preview").catch(e => {
            console.warn("preview request failed", e);
            if (hubConnection.state !== SignalR.HubConnectionState.Connected)
                st.previewRequest.value = true;
        });
        st.previewRequest.value = false;
    }
});

effect(() => {
    st.playRequest.value = st.playing.value != PlayState.None;
});

effect(() => {
    if (st.playRequest.value)
    {
        let playPointer = Math.max(0, Math.min(st.playPointer.peek(), st.history.length - 1));
        const pv =  st.playing.peek();
        switch (pv)
        {
            case PlayState.Forward: case PlayState.NextForward: case PlayState.SeekForward:
                if (pv == PlayState.SeekForward)
                    playPointer = st.history.length - 1;
                else
                    playPointer += 1;

                if (playPointer < st.history.length)
                {
                    showSceneAt(playPointer);
                    batch(() => {
                        st.playPointer.value = playPointer;
                        st.playRequest.value = false;
                        st.playing.value = pv == PlayState.NextForward || pv == PlayState.SeekForward ? PlayState.None : pv;
                    })
                }
                else
                    batch(() => {
                        st.playPointer.value = st.history.length - 1;
                        st.playing.value = (pv == PlayState.Forward || pv == PlayState.NextForward) && st.computing.peek() ? PlayState.ForwardWaiting : PlayState.None;
                        st.playRequest.value = false;
                    });
                break;
            case PlayState.ForwardWaiting: {
                //Follow the newest frame that has actually ARRIVED.
                //
                //This used to be `playPointer + 1`, unbounded - the only branch here that does not
                //check where it is going. At the end of a run the playhead therefore came to rest
                //one past the last frame. Nothing threw, because every reader clamps, but the
                //transport was left with "Previous frame" disabled over a perfectly good history
                //and the clock reporting a step that did not exist.
                //
                //`+ 1` is also no longer the right target. The timeline is filled in from the run
                //index while the run computes, so there are entries AHEAD of what has been sent -
                //stored frames with no payload. Advancing onto one of those would put the playhead
                //on a frame the preview handler is not drawing and make the clock run ahead of the
                //picture. The transport should behave like a player buffering: the slider spans
                //what exists, the playhead sits on what has arrived.
                const arrived = lastReceivedIndex >= 0 && lastReceivedIndex < st.history.length
                    ? lastReceivedIndex
                    : Math.min(playPointer + 1, st.history.length - 1);
                const more = st.computing.peek();
                batch(() => {
                    st.playPointer.value = Math.max(0, arrived);
                    st.playRequest.value = false;
                    //Nothing further is coming, so stop waiting for it. Staying armed is what left
                    //a finished run permanently "playing".
                    st.playing.value = more ? PlayState.ForwardWaiting : PlayState.None;
                });
                break;
            }
            case PlayState.Backward: case PlayState.NextBackward: case PlayState.SeekBackward:
                if (pv == PlayState.SeekBackward)
                    playPointer = 0;
                else
                    playPointer -= 1;

                if (playPointer >= 0)
                {
                    batch(() => {
                        st.playPointer.value = playPointer;
                        showSceneAt(playPointer);
                        st.playRequest.value = false;
                        st.playing.value = pv == PlayState.Backward ? PlayState.Backward : PlayState.None;
                    });
                }
                else
                    batch(() => {
                        st.playPointer.value = 0;
                        st.playing.value = PlayState.None;
                        st.playRequest.value = false;
                    });
                break;
            case PlayState.Seek:
                showSceneAt(playPointer);
                batch(() => {
                    st.playRequest.value = false;
                    st.playing.value = PlayState.None;
                })
                break;
            default: st.playRequest.value = false;
        }
    }
});

/**
 * How many of the most recent frames stay uncompressed.
 *
 * Compression is what makes retrieval asynchronous, and asynchrony is what the playback state
 * machine cannot absorb - a preact effect cannot be async and `batch()` cannot span an await.
 * Keeping a window of recent frames raw means sequential playback and single-stepping, which is
 * what the viewport does almost all of the time, never leave the synchronous path at all. Only
 * scrubbing back into old history pays for a decompress.
 */
const HOT_FRAMES = 8;

/** Frames below this index have been offered to the compressor already. */
let compressFrontier = 0;
let compressing = false;

/** Invalidates any decompression still in flight. Bumped whenever history is replaced. */
let sceneRequestToken = 0;

function resetCompression() {
    compressFrontier = 0;
    compressing = false;
    ++sceneRequestToken;
}

function scheduleCompression() {
    if (compressing || !canDeflate()) return;
    compressing = true;
    //Idle rather than immediate: the frame that just arrived is about to be decoded and drawn,
    //and compressing an older one must not compete with that.
    const run = () => { void drainCompression(); };
    if (typeof requestIdleCallback === 'function') requestIdleCallback(run, { timeout: 2000 });
    else setTimeout(run, 0);
}

async function drainCompression() {
    try {
        while (compressFrontier < st.history.length - HOT_FRAMES) {
            const i = compressFrontier;
            const e = st.history[i];
            if (!e || !e.s || !e.plain) { ++compressFrontier; continue; }

            const packed = await deflateBytes(e.s);
            //A run may have restarted, or a session loaded, while that await was outstanding.
            if (st.history[i] !== e) return;

            //Keep whichever is smaller. Compression can lose on a tiny frame - the six-byte zlib
            //wrapper alone outweighs what there is to gain on the empty t=0 scene - and storing
            //the larger one while flagging it compressed would corrupt it on the way back.
            if (packed.byteLength < e.s.byteLength) {
                st.historySize.value -= e.s.byteLength - packed.byteLength;
                e.s = packed;
                e.plain = false;
            }
            ++compressFrontier;
        }
    } finally { compressing = false; }
}

/** Moves a cache hit to the young end of the LRU. */
function touchCache(i: number): PlantModel[] | undefined {
    const hit = sceneCache.get(i);
    if (!hit) return undefined;
    //A Map iterates in insertion order, which is all the ordering an LRU needs.
    sceneCache.delete(i);
    sceneCache.set(i, hit);
    return hit;
}

/**
 * The scene at `index` if it can be had without awaiting, otherwise undefined.
 *
 * This is the path the playback machine takes: a decoded frame still in the LRU, or a frame in
 * the hot window that has not been compressed yet.
 */
export function sceneAtSync(index: number): PlantModel[] | undefined {
    const i = Math.min(index, st.history.length - 1);
    if (i < 0) return [];

    const hit = touchCache(i);
    if (hit) return hit;

    const e = st.history[i];
    //No payload means it was evicted to the server; not plain means it is compressed. Either way
    //the caller has to go asynchronous.
    if (!e || !e.s || !e.plain) return undefined;

    const decoded = new BinaryReader(e.s).readAgroScene();
    cacheScene(i, decoded);
    return decoded;
}

/**
 * The scene at `index`, decompressing first if it has been packed away.
 *
 * Exported because the offline animation renderer iterates history independently of the playback
 * state machine, and is already asynchronous throughout.
 */
/**
 * Re-fetches an evicted frame from the run store.
 *
 * The bytes are stored deflated and served with `Content-Encoding: deflate`, so the browser
 * inflates them on the way in and there is nothing to undo here. Frames are immutable once
 * written, so the HTTP cache holds the working set without the client managing one.
 */
async function fetchFrame(step: number): Promise<Uint8Array> {
    const runId = st.runId.peek();
    if (!runId) return new Uint8Array(0);
    try {
        const res = await fetch(`${location.protocol}//${BackendURI}/simulation/run/${runId}/frame/${step}?format=2`);
        if (!res.ok) return new Uint8Array(0);
        return new Uint8Array(await res.arrayBuffer());
    } catch (err) {
        console.warn(`Could not fetch frame ${step} of run ${runId}:`, err);
        return new Uint8Array(0);
    }
}

/**
 * The steps recorded in a run's frame index, in order.
 *
 * The format is fixed by `RunStore.WriteFrame` and this is the only thing that reads it, so it is
 * worth stating: **eight little-endian bytes per frame - the addressable step, then the stored
 * size**. Only the step is wanted here; the size is what makes a progress estimate possible
 * without fetching anything.
 *
 * The file is APPENDED to while the run computes, so a read can catch it mid-entry and come back
 * with a length that is not a multiple of eight. A trailing part-entry is dropped rather than
 * decoded: half a step number is a plausible-looking wrong step, and fetching it would return
 * some other frame that decodes perfectly. The frames still arriving on the socket carry the
 * remainder anyway.
 */
export function decodeFrameIndex(buf: Uint8Array): number[] {
    const whole = Math.floor(buf.byteLength / 8);
    if (whole === 0) return [];
    const view = new DataView(buf.buffer, buf.byteOffset, whole * 8);
    const steps: number[] = new Array(whole);
    for (let i = 0; i < whole; ++i) steps[i] = view.getUint32(i * 8, true);
    return steps;
}

/**
 * Brings the timeline into line with what the server has actually stored.
 *
 * This is the eager half of "keep `t` on the client, stream `s`". `GET /run/{id}/index` returns
 * eight bytes per stored frame - the addressable step, then its stored size - so knowing the
 * shape of a 1,440-step run costs about 11 KB against the gigabytes its payloads occupy. Entries
 * the client does not already hold come back with no payload at all and are fetched one at a time
 * as the user scrubs onto them.
 *
 * Called in two situations, which is why it merges rather than replaces:
 *
 *  - after a reload, when the history is empty and the whole timeline has to come back;
 *  - when a run finishes, because the streamed history has HOLES the stored one does not. The
 *    server exports every step; the wire only carries the ones a `Preview` request happened to
 *    land on, so what the browser ended up with depended on how fast it was keeping up. A run
 *    that stored 1,440 frames and streamed 60 left the other 1,380 unreachable from the page
 *    that asked for them. Merging at the end is what makes the stored run and the visible run
 *    the same run.
 *
 * Resident payloads are matched by step. A changed checkpoint revision invalidates its tail.
 */
const frameRevisions = new Map<string, string>();
async function syncTimelineToIndex(runId: string, seekToEnd: boolean): Promise<number> {
    try {
        const res = await fetch(`${location.protocol}//${BackendURI}/simulation/run/${runId}/index`);
        if (!res.ok) return 0;
        const steps = decodeFrameIndex(new Uint8Array(await res.arrayBuffer()));
        if (st.runId.peek() !== runId) return 0;
        const revision = res.headers?.get('X-Agro-Frame-Revision') ?? '0';
        const replaced = frameRevisions.has(runId)
            ? frameRevisions.get(runId) !== revision
            : revision !== '0';
        frameRevisions.set(runId, revision);
        const restart = Number(res.headers?.get('X-Agro-Restart-Step') ?? 0);
        if (steps.length === 0 && !replaced) return 0;
        if (replaced) { clearSceneCache(); resetCompression(); }

        //A union keyed by step, not a positional zip and not a replacement.
        //
        //Keyed by step because the stored sequence has entries the streamed one does not, so
        //index-for-index would pair unrelated frames and mislabel every payload after the first
        //gap. A union rather than "take the server's list" because the reverse is also possible:
        //with a frame stride the client is sent previews of steps that were never stored, and
        //those are real frames it already holds. Neither side is a superset of the other.
        const held = new Map<number, { t: number, s: Uint8Array | undefined, plain: boolean }>();
        for (const e of st.history) if (e && (!replaced || e.t < restart)) held.set(e.t, e);
        for (const t of steps) if (!held.has(t)) held.set(t, { t, s: undefined, plain: true });
        if (!replaced && held.size === st.history.length) return st.history.length;

        const atPointer = st.history[st.playPointer.peek()];
        //Remembered by step before the array is replaced: `lastReceivedIndex` is a position, and
        //every position here is about to move.
        const receivedStep = lastReceivedIndex >= 0 && lastReceivedIndex < st.history.length
            ? st.history[lastReceivedIndex].t : -1;
        const merged = [...held.values()].sort((a, b) => a.t - b.t);

        st.history = merged;
        lastReceivedIndex = receivedStep < 0 ? -1 : merged.findIndex(e => e.t === receivedStep);
        //Where the user was, by step rather than by position - the positions have just moved.
        const restoredPointer = seekToEnd || !atPointer
            ? Math.max(0, merged.length - 1)
            : Math.max(0, merged.findIndex(e => e.t === atPointer.t));

        const pointerMoved = restoredPointer !== st.playPointer.peek();
        batch(() => {
            //Recomputed rather than adjusted. Zero bytes over hundreds of frames is not a
            //contradiction here - every payload is on the server - and it is exactly why the
            //transport bar cannot ask `historySize` whether there is anything to play.
            st.historySize.value = measureHistoryBytes(merged);
            st.historyCount.value = merged.length;
            st.evictedFrames.value = merged.reduce((n, e) => n + (e.s ? 0 : 1), 0);
            st.playPointer.value = restoredPointer;
        });
        //Only when the visible frame actually changed. This now runs repeatedly WHILE a run
        //streams, and redrawing the same frame every couple of seconds would fight the playback
        //machine for the renderer at exactly the scale where that is expensive.
        if (pointerMoved || replaced) showSceneAt(restoredPointer);
        console.log(`Timeline synced to ${merged.length} stored frames of run ${runId}.`);
        return merged.length;
    } catch (err) {
        console.warn(`Could not read the frame index of run ${runId}:`, err);
        return 0;
    }
}

/**
 * Warms the HTTP cache for a frame that is going to be needed shortly.
 *
 * Written for the animation exporter, where every frame used to be a network round trip that the
 * render had to wait for: fetch, inflate, decode, render, repeat, with nothing overlapping. The
 * frames are cached with revalidation, so a later read can reuse them after checking for a
 * resumed run's replacement tail - which means this needs no cache, no eviction and
 * no memory budget of its own. Deliberately fetching the BYTES and not decoding: a decoded scene
 * is thousands of objects and tens of megabytes, and buffering those ahead is what the frame
 * source has always been right to refuse.
 *
 * Silent about everything. A prefetch that fails costs nothing; the real fetch will report it.
 */
export async function prefetchFrame(index: number): Promise<void> {
    const runId = st.runId.peek();
    if (!runId || index < 0 || index >= st.history.length) return;
    const e = st.history[index];
    if (!e || e.s) return;
    try {
        const res = await fetch(`${location.protocol}//${BackendURI}/simulation/run/${runId}/frame/${e.t}?format=2`);
        //Drained rather than discarded: an unread body may never reach the cache.
        if (res.ok) await res.arrayBuffer();
    } catch { /* the real read will say so */ }
}

/**
 * The raw V7 bytes for one history entry, whatever state it is in.
 *
 * Three cases and they are easy to conflate: held raw, held compressed, or evicted to the server.
 * save() and the .prim download both need the raw bytes and neither should have to know which.
 */
async function rawFrameBytes(e: { t: number, s: Uint8Array | undefined, plain: boolean }): Promise<Uint8Array> {
    if (!e.s) {
        const local = localArchiveFrame(e);
        if (local) { const frame = await local; return frame.plain ? frame.bytes : inflateBytes(frame.bytes); }
        return fetchFrame(e.t);
    }
    return e.plain ? e.s : inflateBytes(e.s);
}

export async function sceneAt(index: number): Promise<PlantModel[]> {
    const ready = sceneAtSync(index);
    if (ready) return ready;

    const i = Math.min(index, st.history.length - 1);
    if (i < 0) return [];
    const e = st.history[i];
    if (!e) return [];

    const raw = await rawFrameBytes(e);
    if (!raw.length) return [];
    //If history was replaced while inflating, those bytes describe a scene that no longer
    //exists. Fall back to whatever is there now rather than rendering the old run.
    if (st.history[i] !== e) return sceneAtSync(index) ?? [];

    const decoded = new BinaryReader(raw).readAgroScene();
    cacheScene(i, decoded);
    return decoded;
}

/**
 * Puts the frame at `index` on screen.
 *
 * The synchronous case writes `scene` immediately, which is what every caller inside a `batch()`
 * and the offline renderer's rebuild assumption rely on. A cold frame resolves later, and the
 * token guards the one hazard that introduces: dragging the timeline fast issues several
 * requests, and without it the slowest could land last and leave the wrong frame showing.
 */
function showSceneAt(index: number) {
    const token = ++sceneRequestToken;
    const ready = sceneAtSync(index);
    if (ready) { st.scene.value = ready; return; }

    void sceneAt(index).then(scene => {
        if (token === sceneRequestToken) st.scene.value = scene;
    });
}


/**
 * Decoded scenes, keyed by history index, bounded by total primitive count rather than by
 * entry count.
 *
 * A budget in entries cannot work here: one timestep of a seedling is a few dozen primitives
 * and one of a mature stand is tens of thousands, so any fixed entry count is either useless
 * early or hundreds of megabytes late. Counting primitives makes the cache hold many small
 * frames or few large ones, which is the behaviour wanted in both cases.
 *
 * This does nothing for straight playback, where the pointer only ever advances onto a frame
 * that has not been seen. It is for scrubbing - dragging the timeline back and forth over the
 * same span - and for stepping back a frame to compare, which were re-decoding every time.
 *
 * The budget was 200,000, which made the cache INERT at production scale: a 4,134-plant frame is
 * about 674,000 primitives, so `size > budget` refused every single frame and every scrub paid a
 * full decode. It now sits above one mature frame. Note a decoded primitive costs ~365 bytes -
 * mostly V8 object headers on two small typed arrays - so this budget is roughly 550 MB of heap
 * at worst, and it is deliberately the largest single discretionary allocation the client makes.
 */
export const SCENE_CACHE_PRIMITIVES = 1500000;
const sceneCache = new Map<number, PlantModel[]>();
let sceneCachePrimitives = 0;

function cacheScene(index: number, scene: PlantModel[]) {
    let size = 0;
    for (const plant of scene) size += plant.primitives.length;

    //A single frame larger than the whole budget is not worth evicting everything for.
    if (size > SCENE_CACHE_PRIMITIVES) return;

    dropCachedScene(index);
    sceneCache.set(index, scene);
    sceneCachePrimitives += size;

    while (sceneCachePrimitives > SCENE_CACHE_PRIMITIVES && sceneCache.size > 1) {
        const oldest = sceneCache.keys().next();
        if (oldest.done) break;
        const evicted = sceneCache.get(oldest.value)!;
        for (const plant of evicted) sceneCachePrimitives -= plant.primitives.length;
        sceneCache.delete(oldest.value);
    }
}

function dropCachedScene(index: number) {
    const old = sceneCache.get(index);
    if (!old) return;
    for (const plant of old) sceneCachePrimitives -= plant.primitives.length;
    sceneCache.delete(index);
}

/**
 * Drops every cached scene. Must be called whenever `history` is replaced or truncated, since
 * the cache is keyed by index and a new run reuses indices from zero - a stale entry would
 * render a previous simulation's plants and look like a decoder bug.
 */
export function clearSceneCache() {
    sceneCache.clear();
    sceneCachePrimitives = 0;
}
